import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, Globe, Star, ExternalLink, Calendar, Briefcase, PoundSterling, MessageSquare, User, Heart, Tag, Share2, Facebook, Copy, Check, Building2, Clock, ChevronLeft, ChevronRight, X, Ticket, RefreshCw, Flag } from 'lucide-react';
import { analytics } from '../utils/analytics';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import RecentlyViewedSection from '../components/RecentlyViewedSection';
import { ListingSEO, ReviewsSEO } from '../components/SEO';
import SEO from '../components/SEO';
import { SidebarAd } from '../components/AdBanner';
import ListingCard from '../components/ListingCard';
import SignUpPromptModal from '../components/SignUpPromptModal';
import RichTextRenderer from '../components/RichTextRenderer';
import { Sparkles } from 'lucide-react';
import EnrichmentModal from '../components/EnrichmentModal';
import { getPlaceholderImage } from '../utils/placeholderImages';
import { formatSalary } from '../utils/salaryFormatter';
import { formatJobType, getJobTypeBadgeColor } from '../utils/jobTypeFormatter';
import { formatCondition } from '../utils/conditionFormatter';
import { getListingUrl, slugify } from '../utils/slugify';
import { detailImage, detailHero, detailHeroSrcSet } from '../utils/cloudinaryOptimize';
import SocialLinks from '../components/SocialLinks';
import { FeaturedSocialPosts } from '../components/SocialPostEmbed';
import OpeningHoursStatus from '../components/OpeningHoursStatus';
import ClaimListing from '../components/ClaimListing';
import MobileQuickActions from '../components/MobileQuickActions';

const API = '/api';

// Convert a URL slug back to human-readable text
// e.g. "creative-services" → "Creative Services", "the-nutty-noggin" → "The Nutty Noggin"
const unslugify = (slug) => {
  if (!slug) return '';
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

// Module-level Set to track listings being viewed (persists across re-renders)
const viewsInProgress = new Set();

// Format a relative time string like "3 days ago", "2 months ago"
const formatFreshness = (dateStr) => {
  if (!dateStr) return null;
  const updated = new Date(dateStr);
  const now = new Date();
  const diffMs = now - updated;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return 'Updated today';
  if (diffDays === 1) return 'Updated yesterday';
  if (diffDays < 7) return `Updated ${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `Updated ${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `Updated ${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `Updated ${years} year${years > 1 ? 's' : ''} ago`;
};

// Plan features - what's allowed on each plan per listing type
const PLAN_FEATURES = {
  business: {
    free: { allow_email: false, allow_website: true, allow_tags: false, allow_opening_hours: false, max_images: 1 },
    enhanced: { allow_email: true, allow_website: true, allow_tags: true, allow_opening_hours: true, max_images: 5 },
    premium: { allow_email: true, allow_website: true, allow_tags: true, allow_opening_hours: true, max_images: 10 },
  },
  job: {
    free: { allow_email: true, allow_website: true, allow_tags: false, max_images: 1 },
    enhanced: { allow_email: true, allow_website: true, allow_tags: true, max_images: 3 },
    premium: { allow_email: true, allow_website: true, allow_tags: true, max_images: 5 },
  },
  event: {
    free: { allow_email: false, allow_website: true, allow_tags: false, max_images: 1 },
    enhanced: { allow_email: true, allow_website: true, allow_tags: true, max_images: 3 },
    premium: { allow_email: true, allow_website: true, allow_tags: true, max_images: 5 },
  },
  classified: {
    free: { allow_email: false, allow_website: false, allow_tags: false, max_images: 1 },
    enhanced: { allow_email: true, allow_website: true, allow_tags: true, max_images: 3 },
    premium: { allow_email: true, allow_website: true, allow_tags: true, max_images: 5 },
  },
};

// Check if a feature is allowed for a listing based on its plan
const isPlanFeatureAllowed = (listing, feature) => {
  if (!listing) return false;
  const type = listing.listing_type || 'business';
  const plan = listing.plan || 'free';
  const typeFeatures = PLAN_FEATURES[type];
  if (!typeFeatures) return false;
  const planFeatures = typeFeatures[plan] || typeFeatures.free;
  return planFeatures[feature] ?? false;
};

// Format seller name to show only first name and first letter of last name
const formatSellerName = (fullName) => {
  if (!fullName) return 'Anonymous';
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0];
  // Return first name + first letter of last name
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
};

// Format event date - handles single dates and ranges
// For same year ranges: "20 January - 30 January 2026"
// For different year ranges: "20 December 2025 - 10 January 2026"
// Format category slug to display name (e.g. "markets_fairs" -> "Markets Fairs")
const formatCategory = (cat) => {
  if (!cat) return '';
  return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Format HH:MM to 12-hour time (e.g. "14:30" -> "2:30 PM")
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  return `${h}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const formatEventDate = (dateStr) => {
  if (!dateStr) return null;
  
  // Check if it's a date range (contains " - ")
  if (dateStr.includes(' - ')) {
    const parts = dateStr.split(' - ');
    if (parts.length === 2) {
      const startStr = parts[0].trim();
      const endStr = parts[1].trim();
      
      // Try to parse both dates
      const startDate = new Date(startStr);
      const endDate = new Date(endStr);
      
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        
        if (startYear === endYear) {
          // Same year: "20 January - 30 January 2026"
          const startFormatted = startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
          const endFormatted = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
          return `${startFormatted} - ${endFormatted}`;
        } else {
          // Different years: "20 December 2025 - 10 January 2026"
          const startFormatted = startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
          const endFormatted = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
          return `${startFormatted} - ${endFormatted}`;
        }
      }
      // If parsing failed, return the original string
      return dateStr;
    }
  }
  
  // Single date
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  return dateStr;
};

const ListingDetail = () => {
  // Support both old URL format (/listing/:id) and new SEO format (/listing/:townSlug/:categorySlug/:titleSlug)
  const { id, townSlug, categorySlug, titleSlug } = useParams();
  const isNewUrlFormat = !!(townSlug && categorySlug && titleSlug);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Use preloaded listing data if available (from sync XHR in index.html).
  // This means React's FIRST render already has real data — no loading flash.
  const preloaded = typeof window !== 'undefined' ? window.__LISTING_DATA__ : null;
  const [listing, setListing] = useState(preloaded || null);
  const [listingId, setListingId] = useState(preloaded?.id || id || null);
  const [reviews, setReviews] = useState([]);
  const [ssgData, setSsgData] = useState(() => {
    // Try reading inline SSG data injected by the SSG build process (synchronous, no network needed).
    try {
      const el = document.getElementById('ssg-listing-data');
      if (el) {
        const parsed = JSON.parse(el.textContent);
        if (parsed && parsed.title) return parsed;
      }
    } catch (_) { /* ignore parse errors */ }
    // Fallback: use preloaded data as SSG data (for the !listing fallback's hasRealData check)
    if (preloaded) return preloaded;
    return null;
  });
  const [associatedJobs, setAssociatedJobs] = useState([]);
  const [similarItems, setSimilarItems] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(!preloaded);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  // Watchlist state
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [signUpVariant, setSignUpVariant] = useState('watchlist');
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  // Offer state
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);
  // Share state
  const [linkCopied, setLinkCopied] = useState(false);

  // Report state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportDone, setReportDone] = useState(false);

  const REPORT_REASONS = [
    { value: 'adult_content', label: 'Adult or inappropriate content' },
    { value: 'scam', label: 'Scam or fraudulent listing' },
    { value: 'wrong_category', label: 'Wrong category' },
    { value: 'spam', label: 'Spam' },
    { value: 'offensive', label: 'Offensive or hateful content' },
    { value: 'illegal', label: 'Illegal goods or services' },
    { value: 'duplicate', label: 'Duplicate listing' },
    { value: 'other', label: 'Other' },
  ];

  const handleSubmitReport = async () => {
    if (!reportReason) return;
    setReportSubmitting(true);
    try {
      await axios.post(`${API}/listings/${listingId}/report`, {
        reason: reportReason,
        details: reportDetails.slice(0, 500),
      });
      setReportDone(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleOpenReport = () => {
    setReportReason('');
    setReportDetails('');
    setReportDone(false);
    setReportOpen(true);
  };
  // Review prompt state
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const reviewPromptDismissed = useRef(false);
  // Image gallery state
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [imageOrientation, setImageOrientation] = useState('landscape'); // 'landscape' | 'portrait' | 'square'
  // AI Enrichment state
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Get all images (main + additional), with placeholder fallback
  const getAllImages = () => {
    if (!listing) return [];
    const images = [];
    if (listing.image_url) {
      images.push(detailImage(listing.image_url));
    }
    if (listing.additional_images && listing.additional_images.length > 0) {
      images.push(...listing.additional_images.map(img => detailImage(img)));
    }
    // If no images at all, use category-specific placeholder
    if (images.length === 0) {
      images.push(getPlaceholderImage(listing.listing_type, listing.category));
    }
    return images;
  };

  // Quick contact message templates
  const quickMessages = [
    "Hi, is this still available?",
    "Hi, what's the lowest you'd accept?",
    "Hi, where can I collect this from?",
    "Hi, can you tell me more about this item?"
  ];

  // Track if view increment is in progress for this component instance
  const viewInProgressRef = useRef(false);

  // Track recently viewed listings in localStorage
  const trackRecentlyViewed = (listingData) => {
    if (!listingData?.id) return;
    
    try {
      const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      
      // Remove if already exists (to move to front)
      const filtered = recentlyViewed.filter(item => item.id !== listingData.id);
      
      // Add to front with minimal data
      const newItem = {
        id: listingData.id,
        title: listingData.title,
        listing_type: listingData.listing_type,
        price: listingData.price || listingData.classified_price,
        town: listingData.town,
        category: listingData.category,
        image_url: listingData.image_url,
        rating: listingData.rating,
        review_count: listingData.review_count,
        viewedAt: new Date().toISOString(),
        // Job-specific fields
        salary: listingData.salary,
        hide_salary: listingData.hide_salary,
        is_remote: listingData.is_remote,
        is_hybrid: listingData.is_hybrid,
      };
      
      filtered.unshift(newItem);
      
      // Keep only last 10 items
      const trimmed = filtered.slice(0, 10);
      
      localStorage.setItem('recentlyViewed', JSON.stringify(trimmed));
    } catch (e) {
      console.error('Failed to track recently viewed:', e);
    }
  };

  // Share functions
  // Use API share endpoint for social media to get proper OG meta tags
  const baseUrl = 'https://discoveressex.co.uk';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const socialShareUrl = listingId ? `${baseUrl}/api/share/listing/${listingId}` : shareUrl;
  
  // Community-focused share text
  const getShareText = () => {
    if (!listing) return 'Check out this listing on Discover Essex!';
    const town = listing.town || 'Essex';
    const category = listing.category || '';
    switch (listing.listing_type) {
      case 'business': return `Found this great ${category.toLowerCase()} in ${town} on Discover Essex! ${listing.title}`;
      case 'job': return `New ${category.toLowerCase()} job in ${town}! ${listing.title} - check it out on Discover Essex`;
      case 'event': return `Don't miss this event in ${town}! ${listing.title} on Discover Essex`;
      case 'classified': return `${listing.title} for sale in ${town} on Discover Essex`;
      default: return `Check out ${listing.title} in ${town} on Discover Essex!`;
    }
  };
  const shareTitle = getShareText();

  const handleCopyLink = async () => {
    // For copy link, use the regular URL (not the API share URL)
    const copyToClipboard = async (text) => {
      // Try the modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (e) {
          console.log('Clipboard API failed, trying fallback:', e);
        }
      }
      
      // Fallback for older browsers or when clipboard API fails
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.setAttribute('readonly', '');
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        textArea.remove();
        return successful;
      } catch (e) {
        console.log('Fallback copy failed:', e);
        return false;
      }
    };

    const success = await copyToClipboard(shareUrl);
    if (success) {
      setLinkCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      // Show URL in a prompt so user can copy manually
      const manualCopy = window.prompt('Copy this link:', shareUrl);
      if (manualCopy !== null) {
        toast.info('Please copy the link from your browser address bar');
      }
    }
  };

  const handleShareFacebook = () => {
    analytics.shareClicked('facebook', listing?.listing_type);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(socialShareUrl)}&quote=${encodeURIComponent(shareTitle)}`, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    analytics.shareClicked('twitter', listing?.listing_type);
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(socialShareUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank', 'width=600,height=400');
  };

  const handleShareWhatsApp = () => {
    analytics.shareClicked('whatsapp', listing?.listing_type);
    window.open(`https://wa.me/?text=${encodeURIComponent(shareTitle + '\n\n' + socialShareUrl)}`, '_blank');
  };

  const handleShareEmail = () => {
    analytics.shareClicked('email', listing?.listing_type);
    const subject = encodeURIComponent(listing?.title ? `${listing.title} on Discover Essex` : 'Check this out on Discover Essex');
    const body = encodeURIComponent(`${shareTitle}\n\n${socialShareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  useEffect(() => {
    // Reset state when URL params change to prevent stale data
    setListing(null);
    setListingId(null);
    setLoading(true);
    setSimilarItems([]);
    setReviews([]);
    
    const loadListing = async () => {
      try {
        // Try async preload ONLY if it matches the current URL
        if (window.__listingPreload) {
          const preloadResult = await window.__listingPreload;
          // Consume preload — only use once
          window.__listingPreload = null;
          if (preloadResult && preloadResult.title) {
            // Verify preloaded data matches current URL params
            const matchesUrl = (isNewUrlFormat && preloadResult.title_slug === titleSlug) ||
                               (!isNewUrlFormat && preloadResult.id === id);
            if (matchesUrl) {
              setListing(preloadResult);
              setListingId(preloadResult.id);
              trackRecentlyViewed(preloadResult);
              setLoading(false);
              return;
            }
          }
        }
        // Fallback to regular API fetch
        await fetchListing();
      } catch (e) {
        // Silent fail — don't log to console (Google's renderer captures console.error as page errors)
        setLoading(false);
      }
    };
    loadListing();
  }, [id, townSlug, categorySlug, titleSlug]);

  // Fetch reviews, similar items, and increment view when we have a listing ID
  useEffect(() => {
    if (!listingId) return;
    
    fetchReviews();
    fetchSimilarItems();
    
    // Increment view count only once per listing
    // Check both sessionStorage (persists across page loads in same session)
    // and module-level Set (prevents double-fire from React Strict Mode)
    const viewedKey = `viewed_listing_${listingId}`;
    const alreadyViewed = sessionStorage.getItem(viewedKey);
    const inProgress = viewsInProgress.has(listingId);
    
    if (!alreadyViewed && !inProgress && !viewInProgressRef.current) {
      viewInProgressRef.current = true;
      viewsInProgress.add(listingId);
      incrementView().finally(() => {
        sessionStorage.setItem(viewedKey, 'true');
        // Don't remove from viewsInProgress - keep it to prevent future double-calls
      });
    }
  }, [listingId]);

  useEffect(() => {
    // Fetch associated jobs when listing is loaded and is a business
    if (listing?.listing_type === 'business' && listing?.show_jobs_on_profile) {
      fetchAssociatedJobs();
    }
    // Check watchlist status when listing is loaded and user is logged in
    if (listing && user) {
      checkWatchlistStatus();
    }
  }, [listing, user]);

  // Review prompt: show after 30 seconds on a business listing for logged-in non-owners
  useEffect(() => {
    if (!listing || listing.listing_type !== 'business' || !user || listing.owner_id === user.id) return;
    if (reviewPromptDismissed.current) return;
    const timer = setTimeout(() => {
      if (!reviewPromptDismissed.current) setShowReviewPrompt(true);
    }, 30000);
    return () => clearTimeout(timer);
  }, [listing, user]);

  // Listen for signup prompt events from MobileQuickActions
  useEffect(() => {
    const handleMobileSendMessage = () => {
      if (user && listing?.owner_id !== user.id) {
        // Logged in — open message form
        setShowMessageForm(true);
        const contactSection = document.querySelector('[data-testid="contact-section"]');
        if (contactSection) contactSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Not logged in — show signup modal
        setSignUpVariant('message');
        setShowSignUpModal(true);
      }
    };
    window.addEventListener('mobile-send-message', handleMobileSendMessage);
    return () => window.removeEventListener('mobile-send-message', handleMobileSendMessage);
  }, [user, listing]);



  const incrementView = async () => {
    if (!listingId) return;
    try {
      await axios.post(`${API}/listings/${listingId}/view`);
    } catch (error) {
      // Silently fail - view counting is not critical
      console.log('View increment failed:', error);
    }
  };

  const fetchListing = async (retryCount = 0) => {
    try {
      let response;
      if (isNewUrlFormat) {
        response = await axios.get(`${API}/listings/by-slug/${townSlug}/${categorySlug}/${titleSlug}`, { timeout: 5000 });
      } else if (id) {
        response = await axios.get(`${API}/listings/${id}`, { timeout: 5000 });
      } else {
        throw new Error('No listing identifier provided');
      }
      
      const listingData = response.data;
      
      // Handle 301 redirect for listings that moved (e.g. Essex → London)
      if (listingData.redirect_to) {
        navigate(listingData.redirect_to, { replace: true });
        return;
      }
      
      setListing(listingData);
      setListingId(listingData.id);
      
      // If using old URL format, redirect to new SEO-friendly URL
      if (!isNewUrlFormat && listingData) {
        const newUrl = getListingUrl(listingData);
        window.history.replaceState(null, '', newUrl);
      }
      
      trackRecentlyViewed(listingData);
    } catch (error) {
      // Retry on network failure, server errors, or rate limiting
      if (retryCount < 2 && (!error.response || error.response.status >= 500 || error.response.status === 429)) {
        const delay = error.response?.status === 429 ? 2000 : 500;
        await new Promise(r => setTimeout(r, delay));
        return fetchListing(retryCount + 1);
      }
      // Silent fail — Google's renderer captures console.error as page issues
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    if (!listingId) return;
    try {
      const response = await axios.get(`${API}/reviews/${listingId}`);
      setReviews(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Silent fail for reviews
    }
  };

  const fetchSimilarItems = async () => {
    if (!listingId) return;
    try {
      const response = await axios.get(`${API}/listings/${listingId}/similar?limit=6`);
      setSimilarItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch similar items:', error);
    }
  };

  const checkWatchlistStatus = async () => {
    const actualId = listing?.id || listingId || id;
    if (!actualId) return;
    try {
      const response = await axios.get(`${API}/watchlist/check/${actualId}`);
      setInWatchlist(response.data.in_watchlist);
    } catch (error) {
      console.error('Failed to check watchlist status:', error);
    }
  };

  const handleWatchlistToggle = async () => {
    if (!user) {
      setSignUpVariant('watchlist');
      setShowSignUpModal(true);
      return;
    }
    
    const actualId = listing?.id || listingId || id;
    if (!actualId) return;
    
    setWatchlistLoading(true);
    try {
      if (inWatchlist) {
        await axios.delete(`${API}/watchlist/${actualId}`);
        setInWatchlist(false);
        toast.success('Removed from watchlist');
      } else {
        await axios.post(`${API}/watchlist`, { listing_id: actualId });
        setInWatchlist(true);
        toast.success('Added to watchlist');
        // Show review prompt after watchlist add (if not already shown)
        if (!reviewPromptDismissed.current && listing?.listing_type === 'business') {
          setTimeout(() => { if (!reviewPromptDismissed.current) setShowReviewPrompt(true); }, 2000);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update watchlist');
    } finally {
      setWatchlistLoading(false);
    }
  };

  const handleMakeOffer = async (e) => {
    e.preventDefault();
    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }
    
    const actualId = listing?.id || listingId || id;
    if (!actualId) return;
    
    setSendingOffer(true);
    try {
      await axios.post(`${API}/offers`, {
        listing_id: actualId,
        offer_amount: parseFloat(offerAmount),
        message: offerMessage
      });
      toast.success('Offer submitted successfully! The seller will be notified.');
      setShowOfferForm(false);
      setOfferAmount('');
      setOfferMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit offer');
    } finally {
      setSendingOffer(false);
    }
  };

  const fetchAssociatedJobs = async () => {
    const actualId = listing?.id || listingId || id;
    if (!actualId) return;
    try {
      const response = await axios.get(`${API}/listings/${actualId}/jobs`);
      const jobs = response.data?.jobs || response.data;
      setAssociatedJobs(Array.isArray(jobs) ? jobs : []);
    } catch (error) {
      console.error('Failed to fetch associated jobs:', error);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setSignUpVariant('watchlist');
      setShowSignUpModal(true);
      return;
    }
    
    // Use listingId state which is set after fetching (works with both URL formats)
    const actualListingId = listing?.id || listingId || id;
    if (!actualListingId) {
      toast.error('Unable to submit review - listing not found');
      return;
    }
    
    try {
      await axios.post(`${API}/reviews`, {
        listing_id: actualListingId,
        rating,
        comment,
      });
      toast.success('Review submitted! It will be visible once approved by our moderators.');
      analytics.reviewSubmitted(rating);
      setComment('');
      setRating(5);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit review';
      toast.error(errorMsg);
    }
  };

  const handleContactClick = async () => {
    const actualId = listing?.id || listingId || id;
    if (!actualId) return;
    try {
      await axios.post(`${API}/listings/${actualId}/click`);
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  /**
   * Track an engagement click on the listing, categorised by surface.
   *   - 'contact'        : phone / email / website reveal (handled by handleContactClick)
   *   - 'social_profile' : click on the Facebook/Instagram/etc. icon strip
   *   - 'featured_post'  : click on the embedded Instagram / TikTok / X featured post
   *
   * Results surface on the Premium business's own Dashboard under "Featured Post Clicks".
   */
  const trackEngagement = async (eventType) => {
    const actualId = listing?.id || listingId || id;
    if (!actualId) return;
    try {
      await axios.post(`${API}/listings/${actualId}/click?event_type=${encodeURIComponent(eventType)}`);
    } catch (error) {
      console.error('Failed to track engagement:', error);
    }
  };

  const renderStars = (currentRating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${star <= currentRating ? 'fill-[#D4A017] text-[#D4A017]' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  // Obfuscate phone number for marketplace listings (anti-scraping)
  const obfuscatePhone = (phone) => {
    if (!phone) return '';
    // Show first 4 and last 2 characters only
    const cleaned = phone.replace(/\s/g, '');
    if (cleaned.length <= 6) return '••••••';
    return cleaned.slice(0, 4) + '••••' + cleaned.slice(-2);
  };

  // Obfuscate email address (anti-scraping)
  const obfuscateEmail = (email) => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (!domain) return '••••@••••';
    const visibleLocal = localPart.slice(0, 2);
    const visibleDomain = domain.slice(-4);
    return `${visibleLocal}••••@••••${visibleDomain}`;
  };

  const handleRevealPhone = () => {
    // Log phone reveal for analytics/rate limiting
    handleContactClick();
    setPhoneRevealed(true);
  };

  const handleRevealEmail = () => {
    // Log email reveal for analytics
    handleContactClick();
    setEmailRevealed(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageContent.trim()) return;

    if (!user) {
      setSignUpVariant('message');
      setShowSignUpModal(true);
      return;
    }

    // Use the actual listing ID from the listing data, not the URL param
    const actualListingId = listing?.id || listingId || id;
    if (!actualListingId) {
      toast.error('Unable to send message - listing not found');
      return;
    }

    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/messages/send`, {
        listing_id: actualListingId,
        content: messageContent.trim()
      });

      if (response.data.warning) {
        toast.warning(response.data.warning);
      } else {
        toast.success('Message sent successfully!');
        analytics.messageSent();
      }

      setMessageContent('');
      setShowMessageForm(false);
      
      // Navigate to messages
      navigate(`/messages/${response.data.conversation_id}`);
    } catch (error) {
      // Handle Pydantic validation errors (array of error objects) or string errors
      let errorMsg = 'Failed to send message';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error - extract the message
        errorMsg = detail[0]?.msg || detail[0]?.message || errorMsg;
      }
      
      // Check if this is a content moderation error
      if (errorMsg.includes('threatening') || errorMsg.includes('flagged')) {
        toast.error('Your message was NOT sent. It contains content that violates our community guidelines.', {
          duration: 6000,
          description: 'Please revise your message and try again. Repeated violations may result in account restrictions.'
        });
      } else if (errorMsg.includes('rate limit') || errorMsg.includes('limit')) {
        toast.error('Message limit reached', {
          description: errorMsg
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  // Show loading skeleton while fetching
  if (loading) {
    const placeholderTitle = isNewUrlFormat ? unslugify(titleSlug) : '';
    const placeholderTown = isNewUrlFormat ? unslugify(townSlug) : '';
    const placeholderCategory = isNewUrlFormat ? unslugify(categorySlug) : '';
    const baseUrl = 'https://discoveressex.co.uk';
    const pageUrl = isNewUrlFormat
      ? `${baseUrl}/listing/${townSlug}/${categorySlug}/${titleSlug}`
      : `${baseUrl}/listing/${id || ''}`;

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8" data-testid="listing-detail-loading">
        {isNewUrlFormat && (
          <>
            <SEO
              title={`${placeholderTitle} | ${placeholderCategory} in ${placeholderTown}, Essex`}
              description={`${placeholderTitle} is a ${placeholderCategory.toLowerCase()} in ${placeholderTown}, Essex. View contact details, opening hours, reviews and more on Discover Essex.`}
              keywords={`${placeholderTitle}, ${placeholderCategory} ${placeholderTown}, ${placeholderCategory} Essex, local business ${placeholderTown}`}
              url={pageUrl}
              canonicalUrl={pageUrl}
              type="business.business"
            />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": placeholderTitle,
              "description": `${placeholderTitle} — ${placeholderCategory} in ${placeholderTown}, Essex.`,
              "address": { "@type": "PostalAddress", "addressLocality": placeholderTown, "addressRegion": "Essex", "addressCountry": "GB" },
              "url": pageUrl
            })}} />
          </>
        )}
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-sm">
            <a href="/" className="text-slate-500">Home</a>
            <span className="text-slate-400">/</span>
            {isNewUrlFormat ? (
              <>
                <span className="text-slate-500">{placeholderCategory}</span>
                <span className="text-slate-400">/</span>
                <span className="text-slate-700">{placeholderTitle}</span>
              </>
            ) : (
              <span className="text-slate-500">Loading...</span>
            )}
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <article>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  {placeholderTitle || 'Loading listing...'}
                </h1>
                {isNewUrlFormat && (
                  <p className="text-slate-600 mb-4">
                    {placeholderCategory} in {placeholderTown}, Essex
                  </p>
                )}
                <div className="space-y-3 mt-6">
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-full"></div>
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-5/6"></div>
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-4/6"></div>
                </div>
              </article>
            </div>
            <div>
              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                <div className="h-5 bg-slate-200 rounded animate-pulse w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded animate-pulse w-full"></div>
                <div className="h-4 bg-slate-200 rounded animate-pulse w-2/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    // Use SSG data if available, otherwise derive from URL slugs
    const d = ssgData || {};
    const placeholderTitle = d.title || (isNewUrlFormat ? unslugify(titleSlug) : '');
    const placeholderTown = d.town || (isNewUrlFormat ? unslugify(townSlug) : '');
    const placeholderCategory = d.category || (isNewUrlFormat ? unslugify(categorySlug) : '');
    const baseUrl = 'https://discoveressex.co.uk';
    // When we already have real listing data, derive the canonical URL from
    // the listing's CURRENT town/category/title — independent of how the
    // visitor arrived. This is belt-and-braces alongside the SSR 301: even
    // when someone pastes an old URL directly, Google sees the right canonical.
    const pageUrl = isNewUrlFormat
      ? `${baseUrl}/listing/${townSlug}/${categorySlug}/${titleSlug}`
      : `${baseUrl}/listing/${id || ''}`;
    const canonicalUrl = ssgData
      ? `${baseUrl}${getListingUrl(ssgData)}`
      : pageUrl;
    const listingType = d.listing_type || 'business';
    const catPath = listingType === 'job' ? 'jobs' : listingType === 'event' ? 'events' : listingType === 'classified' ? 'marketplace' : 'business';
    const hasRealData = !!ssgData;

    // --- Improvement 1: Category-specific Schema.org types ---
    const schemaTypeMap = {
      'accountants': 'FinancialService', 'financial': 'FinancialService',
      'restaurant': 'Restaurant', 'cafe': 'Restaurant', 'dining': 'Restaurant', 'food': 'Restaurant',
      'plumber': 'Plumber', 'plumbing': 'Plumber',
      'electrician': 'Electrician', 'electrical': 'Electrician',
      'dentist': 'Dentist', 'dental': 'Dentist',
      'doctor': 'Physician', 'medical': 'MedicalBusiness', 'health': 'MedicalBusiness',
      'lawyer': 'Attorney', 'legal': 'LegalService', 'solicitor': 'LegalService',
      'hair': 'HairSalon', 'barber': 'BarberShop', 'beauty': 'BeautySalon', 'spa': 'DaySpa',
      'gym': 'HealthClub', 'fitness': 'HealthClub',
      'hotel': 'Hotel', 'accommodation': 'LodgingBusiness',
      'school': 'School', 'education': 'EducationalOrganization', 'tutor': 'EducationalOrganization',
      'garage': 'AutoRepair', 'mechanic': 'AutoRepair', 'car': 'AutoRepair', 'mot': 'AutoRepair',
      'vet': 'VeterinaryCare', 'pet': 'PetStore',
      'pharmacy': 'Pharmacy', 'optician': 'Optician',
      'estate agent': 'RealEstateAgent', 'property': 'RealEstateAgent',
      'insurance': 'InsuranceAgency',
      'cleaning': 'HousekeepingService',
      'garden': 'HomeAndConstructionBusiness', 'landscap': 'HomeAndConstructionBusiness',
      'builder': 'GeneralContractor', 'construction': 'GeneralContractor', 'roofing': 'RoofingContractor',
      'photography': 'Photographer', 'photo': 'Photographer',
      'florist': 'Florist',
      'travel': 'TravelAgency',
      'it ': 'ProfessionalService', 'tech': 'ProfessionalService', 'computer': 'ProfessionalService',
      'locksmith': 'Locksmith',
    };
    let schemaType = 'LocalBusiness';
    if (listingType === 'job') {
      schemaType = 'JobPosting';
    } else if (listingType === 'event') {
      schemaType = 'Event';
    } else {
      const catLower = placeholderCategory.toLowerCase();
      for (const [keyword, type] of Object.entries(schemaTypeMap)) {
        if (catLower.includes(keyword)) { schemaType = type; break; }
      }
    }

    // Build comprehensive JSON-LD
    let structuredData;

    if (listingType === 'job') {
      // --- JobPosting schema (Google for Jobs) ---
      structuredData = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "title": placeholderTitle,
        "description": d.description || `${placeholderTitle} — ${placeholderCategory} job in ${placeholderTown}, Essex.`,
        "url": pageUrl,
        "datePosted": d.posted_date || d.created_at || new Date().toISOString(),
        "hiringOrganization": {
          "@type": "Organization",
          "name": d.company_name || d.contact_name || "Employer in Essex",
          "sameAs": d.website || undefined,
        },
        "jobLocation": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": placeholderTown,
            "addressRegion": "Essex",
            "addressCountry": "GB",
            ...(d.postcode && { "postalCode": d.postcode }),
            ...(d.address && { "streetAddress": d.address }),
          }
        },
        "employmentType": d.job_type ? d.job_type.toUpperCase().replace(/\s+/g, '_') : "FULL_TIME",
      };
      if (d.salary_min || d.salary_max || d.salary) {
        const salaryVal = d.salary_min || d.salary_max || (typeof d.salary === 'number' ? d.salary : undefined);
        if (salaryVal) {
          structuredData.baseSalary = {
            "@type": "MonetaryAmount",
            "currency": "GBP",
            "value": { "@type": "QuantitativeValue", "value": salaryVal, "unitText": "YEAR" },
          };
          if (d.salary_min && d.salary_max) {
            structuredData.baseSalary.value = { "@type": "QuantitativeValue", "minValue": d.salary_min, "maxValue": d.salary_max, "unitText": "YEAR" };
          }
        }
      }
      if (d.expires_at) structuredData.validThrough = d.expires_at;
      if (d.image_url) structuredData.image = d.image_url;

    } else if (listingType === 'event') {
      // --- Event schema (Google Events) ---
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": placeholderTitle,
        "description": d.description || `${placeholderTitle} — ${placeholderCategory} event in ${placeholderTown}, Essex.`,
        "url": pageUrl,
        "startDate": d.event_date || d.start_date || d.created_at || new Date().toISOString(),
        "location": {
          "@type": "Place",
          "name": d.venue || d.address || placeholderTown,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": placeholderTown,
            "addressRegion": "Essex",
            "addressCountry": "GB",
            ...(d.postcode && { "postalCode": d.postcode }),
            ...(d.address && { "streetAddress": d.address }),
          }
        },
        "organizer": {
          "@type": "Organization",
          "name": d.organizer_name || d.contact_name || "Event Organiser",
          "url": d.website || undefined,
        },
      };
      if (d.end_date || d.event_end_date) structuredData.endDate = d.end_date || d.event_end_date;
      if (d.image_url) structuredData.image = d.image_url;
      if (d.price || d.ticket_price) {
        structuredData.offers = {
          "@type": "Offer",
          "price": String(d.price || d.ticket_price || 0),
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "url": pageUrl,
        };
      }
      if (d.event_status === 'cancelled') structuredData.eventStatus = "https://schema.org/EventCancelled";
      else structuredData.eventStatus = "https://schema.org/EventScheduled";
      structuredData.eventAttendanceMode = "https://schema.org/OfflineEventAttendanceMode";

    } else {
      // --- LocalBusiness schema (default) ---
      structuredData = {
        "@context": "https://schema.org",
        "@type": schemaType,
        "name": placeholderTitle,
        "description": d.description || `${placeholderTitle} — ${placeholderCategory} in ${placeholderTown}, Essex.`,
        "url": pageUrl,
        "address": { "@type": "PostalAddress", "streetAddress": d.address || "", "addressLocality": placeholderTown, "postalCode": d.postcode || "", "addressRegion": "Essex", "addressCountry": "GB" },
        "areaServed": { "@type": "AdministrativeArea", "name": "Essex" },
      };
      if (d.phone) structuredData.telephone = d.phone;
      if (d.email) structuredData.email = d.email;
      if (d.website) structuredData.sameAs = d.website;
      if (d.image_url) structuredData.image = d.image_url;
      if (d.price_range) structuredData.priceRange = d.price_range;
      if (d.rating && d.review_count) {
        structuredData.aggregateRating = { "@type": "AggregateRating", "ratingValue": d.rating, "reviewCount": d.review_count, "bestRating": 5 };
      }
    }
    if (listingType !== 'job' && listingType !== 'event' && d.opening_hours && typeof d.opening_hours === 'object' && Object.keys(d.opening_hours).length > 0) {
      const dayMap = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" };
      structuredData.openingHoursSpecification = Object.entries(d.opening_hours)
        .filter(([, v]) => v && v !== 'Closed')
        .map(([day, hours]) => {
          const parts = String(hours).split('-').map(s => s.trim());
          return { "@type": "OpeningHoursSpecification", "dayOfWeek": dayMap[day.toLowerCase()] || day, "opens": parts[0] || "09:00", "closes": parts[1] || "17:00" };
        });
    }
    if (listingType !== 'job' && listingType !== 'event' && d.reviews && d.reviews.length > 0) {
      structuredData.review = d.reviews.slice(0, 5).map(r => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.author_name || "A customer" },
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating || 5, "bestRating": 5 },
        ...(r.text && { "reviewBody": r.text }),
      }));
    }

    // --- Improvement 2: BreadcrumbList schema ---
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
        { "@type": "ListItem", "position": 2, "name": placeholderCategory, "item": `${baseUrl}/${catPath}` },
        { "@type": "ListItem", "position": 3, "name": placeholderTown, "item": `${baseUrl}/town/${townSlug}` },
        { "@type": "ListItem", "position": 4, "name": placeholderTitle, "item": pageUrl },
      ],
    };

    // --- Improvement 3: Richer FAQ questions ---
    const faqQuestions = [];
    // Always include location question
    faqQuestions.push({
      "@type": "Question",
      "name": `Where is ${placeholderTitle} located?`,
      "acceptedAnswer": { "@type": "Answer", "text": `${placeholderTitle} is located${d.address ? ` at ${d.address}` : ''} in ${placeholderTown}, Essex${d.postcode ? ` (${d.postcode})` : ''}. Find directions and other local ${placeholderCategory.toLowerCase()} listings on Discover Essex.` }
    });
    // Contact question — only if we have real contact data
    if (hasRealData && (d.phone || d.email || d.website)) {
      const contactParts = [];
      if (d.phone) contactParts.push(`by phone at ${d.phone}`);
      if (d.email) contactParts.push(`by email at ${d.email}`);
      if (d.website) contactParts.push(`through their website at ${d.website}`);
      faqQuestions.push({
        "@type": "Question",
        "name": `How can I contact ${placeholderTitle}?`,
        "acceptedAnswer": { "@type": "Answer", "text": `You can contact ${placeholderTitle} ${contactParts.join(', or ')}.` }
      });
    }
    // Services question (when we have a real description)
    if (hasRealData && d.description && d.description.length > 30) {
      faqQuestions.push({
        "@type": "Question",
        "name": `What services does ${placeholderTitle} offer?`,
        "acceptedAnswer": { "@type": "Answer", "text": d.description.substring(0, 300) + (d.description.length > 300 ? '...' : '') }
      });
    }
    // Opening hours question
    if (hasRealData && d.opening_hours && typeof d.opening_hours === 'object' && Object.keys(d.opening_hours).length > 0) {
      const hoursSummary = Object.entries(d.opening_hours)
        .filter(([, v]) => v && v !== 'Closed')
        .map(([day, hours]) => `${day.charAt(0).toUpperCase() + day.slice(1)}: ${hours}`)
        .join(', ');
      faqQuestions.push({
        "@type": "Question",
        "name": `What are the opening hours of ${placeholderTitle}?`,
        "acceptedAnswer": { "@type": "Answer", "text": hoursSummary || `Please contact ${placeholderTitle} directly for their current opening hours.` }
      });
    }
    // Reviews question
    if (hasRealData && d.rating > 0 && d.review_count > 0) {
      faqQuestions.push({
        "@type": "Question",
        "name": `What do customers say about ${placeholderTitle}?`,
        "acceptedAnswer": { "@type": "Answer", "text": `${placeholderTitle} has a rating of ${Number(d.rating).toFixed(1)} out of 5 based on ${d.review_count} customer review${d.review_count !== 1 ? 's' : ''} on Discover Essex.` }
      });
    }

    const faqData = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqQuestions };

    const ssgReviews = d.reviews || [];

    // --- Improvement 6: Improved meta description ---
    // Use AI-enriched SEO fields when available
    const seoTitle = (hasRealData && d.seo_title) ? d.seo_title : `${placeholderTitle} | ${placeholderCategory} in ${placeholderTown}, Essex`;
    const metaDescParts = [`${placeholderTitle} - ${placeholderCategory} in ${placeholderTown}, Essex`];
    if (hasRealData && d.phone) metaDescParts.push(`Tel: ${d.phone}`);
    if (hasRealData && d.postcode) metaDescParts.push(d.postcode);
    if (hasRealData && d.description) {
      metaDescParts.push(d.description.substring(0, 100));
    } else {
      metaDescParts.push(`View contact details, reviews and more on Discover Essex.`);
    }
    const metaDesc = (hasRealData && d.seo_description) ? d.seo_description : metaDescParts.join('. ').substring(0, 300);
    const seoKeywords = (hasRealData && d.seo_keywords && d.seo_keywords.length)
      ? `${placeholderTitle}, ${d.seo_keywords.join(', ')}`
      : `${placeholderTitle}, ${placeholderCategory} ${placeholderTown}, ${placeholderCategory} Essex, ${placeholderTown} ${placeholderCategory.toLowerCase()}${d.tags && d.tags.length ? ', ' + d.tags.join(', ') : ''}`;

    // Build FAQ JSON-LD — use enriched FAQs if available, otherwise generated ones
    const enrichedFaqs = (hasRealData && d.seo_faqs && d.seo_faqs.length) ? d.seo_faqs : [];
    const combinedFaqQuestions = enrichedFaqs.length > 0
      ? enrichedFaqs.map(f => ({ "@type": "Question", "name": f.question, "acceptedAnswer": { "@type": "Answer", "text": f.answer } }))
      : faqQuestions;

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 overflow-x-hidden" data-testid="listing-detail-page">
        {isNewUrlFormat && (
          <>
            <SEO
              title={seoTitle}
              description={metaDesc}
              keywords={seoKeywords}
              url={pageUrl}
              canonicalUrl={canonicalUrl}
              type="business.business"
              image={d.image_url || ''}
            />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": combinedFaqQuestions }) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }} />
          </>
        )}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          {/* Improvement 5: Semantic breadcrumb nav */}
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
            <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a href="/" className="text-slate-500 hover:text-slate-700" itemProp="item"><span itemProp="name">Home</span></a>
              <meta itemProp="position" content="1" />
            </span>
            <span className="text-slate-400">/</span>
            <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a href={`/${catPath}`} className="text-slate-500 hover:text-slate-700" itemProp="item"><span itemProp="name">{placeholderCategory}</span></a>
              <meta itemProp="position" content="2" />
            </span>
            <span className="text-slate-400">/</span>
            <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a href={`/town/${townSlug}`} className="text-slate-500 hover:text-slate-700" itemProp="item"><span itemProp="name">{placeholderTown}</span></a>
              <meta itemProp="position" content="3" />
            </span>
            <span className="text-slate-400">/</span>
            <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-slate-700 font-medium" itemProp="name">{placeholderTitle}</span>
              <meta itemProp="position" content="4" />
            </span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Main article content */}
              <article itemScope itemType={`https://schema.org/${schemaType}`}>
                <meta itemProp="name" content={placeholderTitle} />
                <meta itemProp="url" content={pageUrl} />
                {d.image_url && <meta itemProp="image" content={d.image_url} />}

                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{placeholderTitle}</h1>
                <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{placeholderTown}, Essex</span>
                  <Badge variant="secondary">{placeholderCategory}</Badge>
                  {hasRealData && d.rating > 0 && (
                    <span className="inline-flex items-center gap-1" itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      <span itemProp="ratingValue">{Number(d.rating).toFixed(1)}</span>
                      <span>(<span itemProp="reviewCount">{d.review_count}</span> {d.review_count === 1 ? 'review' : 'reviews'})</span>
                      <meta itemProp="bestRating" content="5" />
                    </span>
                  )}
                </div>

                {/* Improvement 4: Real description — no filler paragraph */}
                <div className="prose prose-slate max-w-none" itemProp="description">
                  {hasRealData && d.description ? (
                    <>
                      <p>{d.description}</p>
                      {d.tags && d.tags.length > 0 && (
                        <p className="text-sm text-slate-500">
                          Services: {d.tags.join(', ')}
                        </p>
                      )}
                    </>
                  ) : (
                    <p>{placeholderTitle} is a {placeholderCategory.toLowerCase()} based in {placeholderTown}, Essex.
                      View their full contact details, opening hours, customer reviews and more on Discover Essex.</p>
                  )}
                </div>
              </article>

              {/* Opening Hours */}
              {hasRealData && d.opening_hours && typeof d.opening_hours === 'object' && Object.keys(d.opening_hours).length > 0 && (
                <section aria-labelledby="opening-hours-heading">
                  <h2 id="opening-hours-heading" className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-slate-600" /> Opening Hours
                  </h2>
                  {d.opening_hours.open_24_7 ? (
                    <div className="bg-white rounded-xl border border-emerald-200 p-4 flex items-center gap-2" data-testid="hours-open-247">
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                      <span className="text-emerald-700 font-medium text-sm">Open 24 hours, 7 days a week</span>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                        const hrs = d.opening_hours[day] || d.opening_hours[day.toLowerCase()];
                        let display, isClosed, is24h;
                        if (!hrs) {
                          display = '—'; isClosed = false; is24h = false;
                        } else if (typeof hrs === 'string') {
                          isClosed = hrs.toLowerCase() === 'closed';
                          is24h = false;
                          display = hrs;
                        } else if (typeof hrs === 'object') {
                          isClosed = !!hrs.closed;
                          is24h = !hrs.closed && hrs.open === '00:00' && hrs.close === '23:59';
                          display = isClosed ? 'Closed' : is24h ? 'Open 24 hours' : (hrs.open && hrs.close ? `${hrs.open} – ${hrs.close}` : '—');
                        } else {
                          display = String(hrs); isClosed = false; is24h = false;
                        }
                        return (
                          <div key={day} className="flex justify-between px-4 py-2 text-sm">
                            <span className="font-medium text-slate-700">{day}</span>
                            <span className={isClosed ? 'text-red-500' : is24h ? 'text-emerald-600 font-medium' : 'text-slate-600'}>{display}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {/* Reviews */}
              {ssgReviews.length > 0 && (
                <section aria-labelledby="reviews-heading">
                  <h2 id="reviews-heading" className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-500" /> Customer Reviews
                  </h2>
                  <div className="space-y-3">
                    {ssgReviews.slice(0, 5).map((review, i) => (
                      <div key={i} className="bg-white rounded-xl border border-slate-200 p-4" itemScope itemType="https://schema.org/Review">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-slate-800 text-sm" itemProp="author">{review.author_name || 'Customer'}</span>
                          <div className="flex gap-0.5" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                            <meta itemProp="ratingValue" content={String(review.rating || 5)} />
                            <meta itemProp="bestRating" content="5" />
                            {[1,2,3,4,5].map(s => (
                              <Star key={s} className={`h-3.5 w-3.5 ${s <= (review.rating || 5) ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
                            ))}
                          </div>
                        </div>
                        {review.text && <p className="text-sm text-slate-600" itemProp="reviewBody">{review.text}</p>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Improvement 3: Richer FAQ section */}
              <section aria-labelledby="faq-heading">
                <h2 id="faq-heading" className="text-lg font-semibold text-slate-900 mb-3">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  <details className="bg-white rounded-xl border border-slate-200 p-4" open>
                    <summary className="font-medium text-slate-800 cursor-pointer">Where is {placeholderTitle} located?</summary>
                    <p className="mt-2 text-sm text-slate-600">
                      {placeholderTitle} is located{hasRealData && d.address ? ` at ${d.address}` : ''} in {placeholderTown}, Essex{hasRealData && d.postcode ? ` (${d.postcode})` : ''}.
                      Find directions and other local {placeholderCategory.toLowerCase()} listings on Discover Essex.
                    </p>
                  </details>
                  {hasRealData && (d.phone || d.email || d.website) && (
                    <details className="bg-white rounded-xl border border-slate-200 p-4" open>
                      <summary className="font-medium text-slate-800 cursor-pointer">How can I contact {placeholderTitle}?</summary>
                      <p className="mt-2 text-sm text-slate-600">
                        You can contact {placeholderTitle}
                        {d.phone ? ` by phone at ${d.phone}` : ''}
                        {d.email ? `${d.phone ? ', or' : ''} by email at ${d.email}` : ''}
                        {d.website ? `. Visit their website at ${d.website}` : ''}.
                      </p>
                    </details>
                  )}
                  {hasRealData && d.description && d.description.length > 30 && (
                    <details className="bg-white rounded-xl border border-slate-200 p-4">
                      <summary className="font-medium text-slate-800 cursor-pointer">What services does {placeholderTitle} offer?</summary>
                      <p className="mt-2 text-sm text-slate-600">{d.description.substring(0, 300)}{d.description.length > 300 ? '...' : ''}</p>
                    </details>
                  )}
                  {hasRealData && d.opening_hours && typeof d.opening_hours === 'object' && Object.keys(d.opening_hours).length > 0 && (
                    <details className="bg-white rounded-xl border border-slate-200 p-4">
                      <summary className="font-medium text-slate-800 cursor-pointer">What are {placeholderTitle}'s opening hours?</summary>
                      <div className="mt-2 text-sm text-slate-600 space-y-1">
                        {d.opening_hours.open_24_7 ? (
                          <p className="text-emerald-700 font-medium">Open 24 hours, 7 days a week</p>
                        ) : (
                          ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                            const hrs = d.opening_hours[day] || d.opening_hours[day.toLowerCase()];
                            if (!hrs) return null;
                            let display;
                            if (typeof hrs === 'string') {
                              display = hrs;
                            } else if (typeof hrs === 'object') {
                              display = hrs.closed ? 'Closed' : (!hrs.closed && hrs.open === '00:00' && hrs.close === '23:59') ? 'Open 24 hours' : (hrs.open && hrs.close ? `${hrs.open} – ${hrs.close}` : null);
                            }
                            return display ? <p key={day}><strong>{day}:</strong> {display}</p> : null;
                          })
                        )}
                      </div>
                    </details>
                  )}
                  {hasRealData && d.rating > 0 && d.review_count > 0 && (
                    <details className="bg-white rounded-xl border border-slate-200 p-4">
                      <summary className="font-medium text-slate-800 cursor-pointer">What do customers say about {placeholderTitle}?</summary>
                      <p className="mt-2 text-sm text-slate-600">
                        {placeholderTitle} has a rating of {Number(d.rating).toFixed(1)} out of 5 based on {d.review_count} customer review{d.review_count !== 1 ? 's' : ''} on Discover Essex.
                      </p>
                    </details>
                  )}
                </div>
              </section>
            </div>

            {/* Sidebar with semantic <address> for contact info */}
            <aside className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Contact Information</h2>
                {/* Improvement 5: Semantic <address> element */}
                <address className="not-italic space-y-3">
                  {hasRealData && d.address && (
                    <div className="flex items-start gap-3 text-sm">
                      <MapPin className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                      <div><p className="font-medium text-slate-500 text-xs uppercase">Address</p><p className="text-slate-800">{d.address}{d.postcode ? `, ${d.postcode}` : ''}</p></div>
                    </div>
                  )}
                  {hasRealData && d.phone && (
                    <div className="flex items-start gap-3 text-sm">
                      <Phone className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                      <div><p className="font-medium text-slate-500 text-xs uppercase">Phone</p><a href={`tel:${d.phone}`} className="text-blue-600 hover:text-blue-700">{d.phone}</a></div>
                    </div>
                  )}
                  {hasRealData && d.email && (
                    <div className="flex items-start gap-3 text-sm">
                      <Mail className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                      <div><p className="font-medium text-slate-500 text-xs uppercase">Email</p><a href={`mailto:${d.email}`} className="text-blue-600 hover:text-blue-700">{d.email}</a></div>
                    </div>
                  )}
                  {hasRealData && d.website && (
                    <div className="flex items-start gap-3 text-sm">
                      <Globe className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                      <div><p className="font-medium text-slate-500 text-xs uppercase">Website</p><a href={d.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">{d.website.replace(/^https?:\/\//, '')}</a></div>
                    </div>
                  )}
                  {!hasRealData && (
                    <p className="text-sm text-slate-600">
                      View the full {placeholderTitle} listing for contact details including phone, email and address.
                    </p>
                  )}
                </address>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-3">More in {placeholderTown}</h2>
                <nav aria-label="Related listings" className="space-y-2 text-sm">
                  <a href={`/${catPath}`} className="block text-blue-600 hover:text-blue-700">Browse all {placeholderCategory} listings</a>
                  <a href={`/town/${townSlug}`} className="block text-blue-600 hover:text-blue-700">Explore {placeholderTown} directory</a>
                  <a href="/business" className="block text-blue-600 hover:text-blue-700">All Essex businesses</a>
                </nav>
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  const getTypeColor = () => {
    switch (listing.listing_type) {
      case 'business': return 'bg-blue-100 text-blue-800';
      case 'event': return 'bg-purple-100 text-purple-800';
      case 'job': return 'bg-green-100 text-green-800';
      case 'classified': return 'bg-orange-100 text-orange-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getMainCategoryLabel = () => {
    switch (listing.listing_type) {
      case 'business': return 'Business';
      case 'event': return 'Event';
      case 'job': return 'Job';
      case 'classified': return 'Marketplace';
      default: return 'Listing';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 overflow-x-hidden" data-testid="listing-detail-page">
      {/* SEO Meta Tags */}
      <ListingSEO listing={listing} />
      {listing?.listing_type === 'business' && reviews.length > 0 && (
        <ReviewsSEO listing={listing} reviews={reviews} />
      )}
      
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Breadcrumb — Home / Type / Town / Category / Title */}
        <nav aria-label="Breadcrumb" className="mb-6" data-testid="listing-breadcrumb">
          <ol className="flex items-center gap-1.5 flex-wrap text-sm">
            <li><Link to="/" className="text-slate-500 hover:text-[#0F2C3E] transition-colors">Home</Link></li>
            <li className="text-slate-400">/</li>
            <li>
              <Link 
                to={
                  listing.listing_type === 'business' ? '/business' :
                  listing.listing_type === 'job' ? '/jobs' :
                  listing.listing_type === 'event' ? '/events' :
                  listing.listing_type === 'classified' ? '/marketplace' : '/'
                } 
                className="text-slate-500 hover:text-[#0F2C3E] transition-colors"
              >
                {listing.listing_type === 'classified' ? 'Marketplace' : 
                 listing.listing_type === 'business' ? 'Businesses' :
                 listing.listing_type === 'job' ? 'Jobs' :
                 listing.listing_type === 'event' ? 'Events' : `${listing.listing_type}s`}
              </Link>
            </li>
            {listing.town && (
              <>
                <li className="text-slate-400">/</li>
                <li>
                  <Link 
                    to={`/town/${listing.town.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                    className="text-slate-500 hover:text-[#0F2C3E] transition-colors"
                  >
                    {listing.town}
                  </Link>
                </li>
              </>
            )}
            {listing.category && (
              <>
                <li className="text-slate-400">/</li>
                <li>
                  <Link 
                    to={listing.listing_type === 'business' ? `/business/${slugify(listing.category)}` : `/${listing.listing_type === 'job' ? 'jobs' : listing.listing_type === 'event' ? 'events' : 'marketplace'}?category=${encodeURIComponent(listing.category)}`}
                    className="text-slate-500 hover:text-[#0F2C3E] transition-colors"
                  >
                    {formatCategory(listing.category)}
                  </Link>
                </li>
              </>
            )}
            <li className="text-slate-400">/</li>
            <li className="text-slate-700 font-medium truncate max-w-[200px]">{listing.title}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Job-specific header (no image) */}
            {listing.listing_type === 'job' ? (
              <>
              {listing.status === 'archived' && (
                <div className="mb-4 p-4 bg-slate-50 border-l-4 border-slate-300 rounded-lg" data-testid="archived-listing-banner">
                  <p className="text-sm text-slate-600 mb-2">
                    <strong>This listing has been archived</strong> and is no longer actively listed.
                  </p>
                  <Link 
                    to="/jobs"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F2C3E] hover:underline"
                    data-testid="archived-browse-cta"
                  >
                    Browse Jobs
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
              <div className="bg-green-50 rounded-2xl border border-green-100 mb-6 overflow-hidden">
                {/* Job Header */}
                <div className="p-6 border-b border-green-100">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          <Briefcase className="h-3.5 w-3.5 mr-1" />
                          Job
                        </Badge>
                        {listing.plan !== 'free' && !listing.plan?.startsWith('free_') && (
                          <Badge className="bg-[#D4A017] text-white">
                            {listing.plan.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                      <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                        {listing.title}
                        {listing.verified && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                aria-label="What does the Verified badge mean?"
                                className="inline-flex items-center justify-center ml-2 w-7 h-7 bg-blue-500 hover:bg-blue-600 rounded-full align-middle cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                                data-testid="verified-badge"
                              >
                                <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-80 p-4"
                              data-testid="verified-badge-popover"
                            >
                              <div className="flex items-start gap-2 mb-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 rounded-full shrink-0 mt-0.5">
                                  <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                                </span>
                                <h4 className="font-semibold text-slate-900 text-sm">Verified by Discover Essex</h4>
                              </div>
                              <p className="text-xs text-slate-600 leading-relaxed mb-3">
                                We&apos;ve confirmed this business&apos;s contact phone, cross-referenced its name and address against Companies House, and verified any trade credentials (e.g. Gas Safe, NICEIC) against the relevant public register. It is not a quality endorsement, but the details are real.
                              </p>
                              <Link
                                to="/how-we-verify"
                                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
                                data-testid="verified-badge-popover-link"
                              >
                                Read our full verification process
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                              </Link>
                            </PopoverContent>
                          </Popover>
                        )}
                      </h1>
                      {listing.company_name && (
                        <div className="flex items-center gap-2 text-lg text-slate-700">
                          <Building2 className="h-5 w-5 text-green-600" />
                          <span className="font-medium">{listing.company_name}</span>
                        </div>
                      )}
                    </div>
                    {/* Watchlist Button */}
                    {listing.status !== 'sold' && (
                      <button
                        onClick={handleWatchlistToggle}
                        disabled={watchlistLoading}
                        className={`p-3 rounded-full transition-all ${
                          inWatchlist 
                            ? 'bg-red-500 text-white hover:bg-red-600' 
                            : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-red-500 border border-slate-200'
                        }`}
                        title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                        data-testid="watchlist-btn"
                      >
                        <Heart className={`h-5 w-5 ${inWatchlist ? 'fill-current' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Key Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-white/50">
                  {/* Salary */}
                  <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                    <div className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Salary</div>
                    <div className="text-lg font-bold text-green-700">
                      {listing.hide_salary ? 'Competitive' : formatSalary(listing.salary)}
                    </div>
                  </div>
                  
                  {/* Location */}
                  <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                    <div className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Location</div>
                    <div className="text-lg font-semibold text-slate-800 flex items-center justify-center gap-1">
                      <MapPin className="h-4 w-4 text-green-600" />
                      {listing.is_remote ? 'Remote' : listing.is_hybrid ? 'Hybrid' : listing.is_outside_essex && listing.region && listing.region !== 'Essex' ? listing.region : listing.town}
                    </div>
                  </div>
                  
                  {/* Job Type */}
                  <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                    <div className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Type</div>
                    <div className="text-lg font-semibold text-slate-800">
                      {formatJobType(listing.job_type) || 'Full Time'}
                    </div>
                  </div>
                  
                  {/* Category */}
                  <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                    <div className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Category</div>
                    <div className="text-base font-semibold text-slate-800 break-words text-center leading-snug">
                      {formatCategory(listing.category)}
                    </div>
                  </div>
                </div>
                
                {/* Work Type Badges */}
                {(listing.is_remote || listing.is_hybrid) && (
                  <div className="px-6 pb-4 flex gap-2">
                    {listing.is_remote && (
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                        🏠 Work from Home
                      </Badge>
                    )}
                    {listing.is_hybrid && (
                      <Badge className="bg-sky-100 text-sky-700 border-sky-200">
                        🏢 Hybrid Working
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </>) : (
            /* Hero Image with Gallery - for non-job listings */
            <div className="relative rounded-2xl overflow-hidden mb-6">
              {/* Smart Image Container - auto-detects orientation.
                  Premium listings may supply `cover_image_url` — a dedicated 16:9 banner. */}
              {(() => {
                const heroSource = selectedImageIndex === 0 && listing.cover_image_url
                  ? listing.cover_image_url
                  : (getAllImages()[selectedImageIndex] || getPlaceholderImage(listing.listing_type, listing.category));
                // When we have a dedicated cover image OR the gallery image is landscape,
                // we use a 16:9 smart-crop (content-aware via Cloudinary `g_auto`) for a crisp hero.
                const useSmartCrop = (selectedImageIndex === 0 && listing.cover_image_url) || imageOrientation === 'landscape';
                const heroUrl = useSmartCrop ? (detailHero(heroSource) || heroSource) : heroSource;
                const heroSrcSet = useSmartCrop ? detailHeroSrcSet(heroSource) : undefined;
                return (
                  <div className="relative w-full h-72 md:h-96">
                    {/* Blurred background fill for non-landscape images */}
                    {!useSmartCrop && imageOrientation !== 'landscape' && (
                      <img
                        src={heroSource}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-60"
                      />
                    )}
                    <img
                      src={heroUrl}
                      srcSet={heroSrcSet}
                      sizes="(max-width: 768px) 100vw, 1200px"
                      alt={listing.title}
                      className={`relative w-full h-full cursor-pointer ${useSmartCrop || imageOrientation === 'landscape' ? 'object-cover' : 'object-contain'} ${listing.status === 'sold' ? 'grayscale' : ''}`}
                      data-testid="listing-image"
                      onClick={() => getAllImages().length > 0 && setShowLightbox(true)}
                      onLoad={(e) => {
                        if (selectedImageIndex === 0 && listing.cover_image_url) return;
                        const { naturalWidth, naturalHeight } = e.target;
                        const ratio = naturalWidth / naturalHeight;
                        if (ratio > 1.2) setImageOrientation('landscape');
                        else if (ratio < 0.85) setImageOrientation('portrait');
                        else setImageOrientation('square');
                      }}
                    />
                  </div>
                );
              })()}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
              
              {/* Image Navigation Arrows */}
              {getAllImages().length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(prev => prev === 0 ? getAllImages().length - 1 : prev - 1);
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(prev => prev === getAllImages().length - 1 ? 0 : prev + 1);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                    {selectedImageIndex + 1} / {getAllImages().length}
                  </div>
                </>
              )}
              
              {/* Floating badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                <Badge className={`${getTypeColor()} capitalize font-medium`}>
                  {getMainCategoryLabel()}
                </Badge>
                {listing.plan !== 'free' && !listing.plan?.startsWith('free_') && (
                  <Badge className="bg-[#D4A017] text-white">
                    {listing.plan.toUpperCase()}
                  </Badge>
                )}
                {listing.marketplace_featured && (
                  <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-none">
                    FEATURED
                  </Badge>
                )}
                {listing.marketplace_urgent && (
                  <Badge className="bg-red-600 text-white border-none">
                    URGENT
                  </Badge>
                )}
                {listing.status === 'sold' && (
                  <Badge className="bg-red-600 text-white font-bold">
                    SOLD
                  </Badge>
                )}
              </div>
              
              {/* Watchlist Button - hide for sold items */}
              {listing.status !== 'sold' && (
                <button
                  onClick={handleWatchlistToggle}
                  disabled={watchlistLoading}
                  className={`absolute top-4 right-4 p-2.5 rounded-full transition-all ${
                    inWatchlist 
                      ? 'bg-red-500 text-white hover:bg-red-600' 
                      : 'bg-white/90 text-slate-600 hover:bg-white hover:text-red-500'
                  }`}
                  title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                  data-testid="watchlist-btn"
                >
                  <Heart className={`h-5 w-5 ${inWatchlist ? 'fill-current' : ''}`} />
                </button>
              )}
            </div>
            )}

            {/* Thumbnail Gallery - only for non-job listings */}
            {listing.listing_type !== 'job' && getAllImages().length > 1 && (
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {getAllImages().map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all bg-white ${
                      selectedImageIndex === index 
                        ? 'border-[#0F2C3E] ring-2 ring-[#0F2C3E]/20' 
                        : 'border-transparent hover:border-slate-300'
                    }`}
                  >
                    <img src={img} alt={`${listing.title} — photo ${index + 1} of ${getAllImages().length}`} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}

            {/* Lightbox Modal - only for non-job listings */}
            {listing.listing_type !== 'job' && showLightbox && (
              <div 
                className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                onClick={() => setShowLightbox(false)}
              >
                <button
                  onClick={() => setShowLightbox(false)}
                  className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
                >
                  <X className="h-6 w-6" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(prev => prev === 0 ? getAllImages().length - 1 : prev - 1);
                  }}
                  className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                
                <img
                  src={getAllImages()[selectedImageIndex]}
                  alt={`${listing.title} — photo ${selectedImageIndex + 1}`}
                  className="max-h-[85vh] max-w-[90vw] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(prev => prev === getAllImages().length - 1 ? 0 : prev + 1);
                  }}
                  className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
                
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 rounded-full text-white">
                  {selectedImageIndex + 1} / {getAllImages().length}
                </div>
              </div>
            )}

            {/* Title and Info Card - only for non-job listings */}
            {listing.listing_type !== 'job' && (
              <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 mb-6">
                <h1 className="text-2xl md:text-3xl font-bold mb-4 text-slate-900" data-testid="listing-title">
                  {listing.title}
                  {listing.listing_type === 'business' && listing.verified && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="What does the Verified badge mean?"
                          className="inline-flex items-center justify-center ml-2 w-7 h-7 bg-blue-500 hover:bg-blue-600 rounded-full align-middle cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                          data-testid="verified-badge"
                        >
                          <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="w-80 p-4"
                        data-testid="verified-badge-popover"
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 rounded-full shrink-0 mt-0.5">
                            <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                          </span>
                          <h4 className="font-semibold text-slate-900 text-sm">Verified by Discover Essex</h4>
                        </div>
                        <p className="text-xs text-slate-600 leading-relaxed mb-3">
                          We&apos;ve confirmed this business&apos;s contact phone, cross-referenced its name and address against Companies House, and verified any trade credentials (e.g. Gas Safe, NICEIC) against the relevant public register. It is not a quality endorsement, but the details are real.
                        </p>
                        <Link
                          to="/how-we-verify"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
                          data-testid="verified-badge-popover-link"
                        >
                          Read our full verification process
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                        </Link>
                      </PopoverContent>
                    </Popover>
                  )}
                </h1>

                {/* Live "Open now / Closed" pill for businesses that publish opening hours */}
                {listing.listing_type === 'business' && listing.opening_hours && isPlanFeatureAllowed(listing, 'allow_opening_hours') && (
                  <div className="mb-4 -mt-2">
                    <OpeningHoursStatus openingHours={listing.opening_hours} />
                  </div>
                )}

                {listing.status === 'pending' && (
                  <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg" data-testid="pending-listing-banner">
                    <p className="text-sm text-yellow-800">
                      <strong>Pending Approval:</strong> This listing is awaiting admin review and is not visible to the public yet. You'll be notified once it's approved.
                    </p>
                  </div>
                )}

                {listing.status === 'archived' && (
                  <div className="mb-4 p-4 bg-slate-50 border-l-4 border-slate-300 rounded-lg" data-testid="archived-listing-banner">
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>This listing has been archived</strong> and is no longer actively listed.
                    </p>
                    <Link 
                      to={listing.listing_type === 'job' ? '/jobs' : '/marketplace'}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F2C3E] hover:underline"
                      data-testid="archived-browse-cta"
                    >
                      Browse {listing.listing_type === 'job' ? 'Jobs' : 'Marketplace'}
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-4 mb-6 pb-6 border-b">
                  {/* Only show reviews for business listings (not events, classifieds, or jobs) */}
                  {listing.listing_type === 'business' && (
                    <>
                      {renderStars(Math.round(listing.rating || 0))}
                      <span className="text-slate-600" data-testid="review-count">
                        {listing.review_count} review{listing.review_count !== 1 ? 's' : ''}
                      </span>
                      <span className="text-slate-300">•</span>
                    </>
                  )}
                  <span className="text-slate-600 flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {listing.is_outside_essex && listing.region && listing.region !== 'Essex' ? listing.region : listing.town}
                  </span>
                  <span className="text-slate-300">•</span>
                  <Badge variant="secondary" className="text-xs">{formatCategory(listing.category)}</Badge>
                  {listing.listing_type === 'event' && listing.event_hosted_by && (
                    <>
                      <span className="text-slate-300">•</span>
                      <span className="text-slate-600 flex items-center gap-1" data-testid="title-hosted-by">
                        <User className="h-4 w-4" />
                        {listing.event_hosted_by}
                      </span>
                    </>
                  )}
                </div>
                {listing.is_outside_essex && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg mb-6" data-testid="non-essex-detail-banner">
                    <Globe className="h-5 w-5 text-blue-500 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Based in {listing.region || 'UK'} — Serves Essex</p>
                      <p className="text-xs text-blue-600 mt-0.5">{listing.essex_service_type === 'remote' ? 'This business works remotely with Essex-based clients' : listing.essex_service_type === 'travel' ? 'This business travels to Essex for work' : 'This business serves clients across the UK including Essex'}</p>
                    </div>
                  </div>
                )}
                {listing.listing_type === 'classified' && (
                  <div className="mb-6">
                    {/* Price & Condition Card - Clean inline layout */}
                    <div className={`rounded-xl p-4 sm:p-6 mb-4 border ${listing.status === 'sold' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                      {listing.status === 'sold' ? (
                        /* Sold State */
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm text-red-500 mb-1 flex items-center gap-2">
                              <Tag className="h-4 w-4" />
                              Status
                            </p>
                            <span className="text-3xl sm:text-4xl font-bold text-red-600">SOLD</span>
                          </div>
                          {listing.sold_at && (
                            <div className="text-right">
                              <p className="text-sm text-red-400 mb-1">Sold On</p>
                              <span className="text-base sm:text-lg font-medium text-slate-900">
                                {new Date(listing.sold_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Active State - Price & Condition inline */
                        <>
                        <div className="flex items-center justify-between gap-4">
                          {/* Price */}
                          <div className="flex-1">
                            <p className="text-sm text-slate-500 mb-1 flex items-center gap-2">
                              <Tag className="h-4 w-4" />
                              Price
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-2xl sm:text-3xl font-bold text-slate-900">
                                {listing.classified_price !== undefined && listing.classified_price !== null
                                  ? listing.classified_price === 0 ? 'Free' : `£${Number(listing.classified_price).toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                                  : listing.price_range || 'Contact for price'
                                }
                              </span>
                              {listing.accepts_offers && listing.classified_price && (
                                <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                                  Accepts Offers
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Condition - Right aligned */}
                          {listing.classified_condition && (
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm text-slate-500 mb-1">Condition</p>
                              <Badge variant="outline" className="capitalize text-sm px-3 py-1.5 font-medium">
                                {formatCondition(listing.classified_condition)}
                              </Badge>
                            </div>
                          )}
                        </div>
                        {listing.created_at && (
                          <p className="text-xs text-slate-400 mt-2" data-testid="classified-listed-date">
                            Listed {new Date(listing.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        )}
                        </>
                      )}
                    </div>
                  
                  {/* Seller Info Card */}
                  <div className="bg-[rgb(255,251,235)] rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0F2C3E] to-[#2a5a75] flex items-center justify-center text-white text-xl font-semibold shadow-md">
                        {listing.owner_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-500" />
                          Listed by {formatSellerName(listing.owner_name)}
                        </p>
                        {listing.owner_member_since && (
                          <p className="text-sm text-slate-500 mt-0.5">
                            Member since {new Date(listing.owner_member_since).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                      <div className="hidden sm:block text-right">
                        <Badge variant="outline" className="text-[#0F2C3E] border-[#0F2C3E]">
                          Private Seller
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Make Offer Section - Only if listing accepts offers */}
                  {listing.accepts_offers && user && listing.owner_id !== user.id && (
                    <div className="mt-4">
                      {!showOfferForm ? (
                        <Button 
                          onClick={() => setShowOfferForm(true)}
                          className="w-full bg-[#D4A017] hover:bg-[#b8890f] text-white"
                          data-testid="make-offer-btn"
                        >
                          <Tag className="h-4 w-4 mr-2" />
                          Make an Offer
                        </Button>
                      ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                            <Tag className="h-4 w-4" />
                            Make an Offer
                          </h4>
                          <form onSubmit={handleMakeOffer} className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                Your Offer (£)
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={offerAmount}
                                onChange={(e) => setOfferAmount(e.target.value)}
                                placeholder={listing.classified_price ? `Asking: £${listing.classified_price}` : 'Enter amount'}
                                required
                                data-testid="offer-amount"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                Message (optional)
                              </label>
                              <Textarea
                                value={offerMessage}
                                onChange={(e) => setOfferMessage(e.target.value)}
                                placeholder="Add a message to the seller..."
                                rows={2}
                                maxLength={200}
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                type="submit" 
                                disabled={sendingOffer}
                                className="flex-1 bg-[#D4A017] hover:bg-[#b8890f]"
                              >
                                {sendingOffer ? 'Submitting...' : 'Submit Offer'}
                              </Button>
                              <Button 
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setShowOfferForm(false);
                                  setOfferAmount('');
                                  setOfferMessage('');
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                            <p className="text-xs text-slate-500">
                              The seller will receive your offer and can accept or decline.
                            </p>
                          </form>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Show badge if accepts offers */}
                  {listing.accepts_offers && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
                      <Tag className="h-4 w-4" />
                      <span>Seller accepts offers</span>
                    </div>
                  )}
                </div>
              )}

              {/* Job specific info - Salary Card */}
              {listing.listing_type === 'job' && (listing.salary || listing.hide_salary) && (
                <div className="mb-6">
                  {/* Salary Card */}
                  <div className="bg-gradient-to-r from-[#4A6741] to-[#5a7a51] rounded-xl p-6 text-white" data-testid="job-salary-card">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      {/* Salary Display */}
                      <div>
                        <p className="text-sm text-white/70 mb-1 flex items-center gap-2">
                          <PoundSterling className="h-4 w-4" />
                          Salary
                        </p>
                        <span className="text-3xl font-bold">
                          {listing.hide_salary ? 'Competitive' : formatSalary(listing.salary)}
                        </span>
                      </div>
                      
                      {/* Job Type Badge */}
                      <div className="text-right">
                        <p className="text-sm text-white/70 mb-1">Position Type</p>
                        <Badge className="bg-white/20 text-white border-white/30 capitalize text-sm px-3 py-1">
                          {listing.job_type ? formatJobType(listing.job_type) : 'Full Time'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Event specific info - Date Card */}
              {listing.listing_type === 'event' && listing.event_date && (
                <div className="mb-6">
                  {/* Event Date Card */}
                  <div className="bg-gradient-to-r from-[#7C3AED] to-[#9333EA] rounded-xl p-6 text-white" data-testid="event-date-card">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      {/* Date Display */}
                      <div>
                        <p className="text-sm text-white/70 mb-1 flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Event Date
                        </p>
                        <span className="text-3xl font-bold">
                          {formatEventDate(listing.event_date)}
                        </span>
                        {/* Time Display */}
                        {(listing.event_start_time || listing.event_end_time) && (
                          <p className="text-base text-white/90 mt-2 flex items-center gap-1.5">
                            <Clock className="h-4 w-4" />
                            {listing.event_start_time && listing.event_end_time
                              ? `${formatTime(listing.event_start_time)} - ${formatTime(listing.event_end_time)}`
                              : listing.event_start_time
                                ? `From ${formatTime(listing.event_start_time)}`
                                : `Until ${formatTime(listing.event_end_time)}`
                            }
                          </p>
                        )}
                        {/* Hosted By Display */}
                        {listing.event_hosted_by && (
                          <p className="text-base text-white/90 mt-2 flex items-center gap-1.5" data-testid="event-hosted-by">
                            <User className="h-4 w-4" />
                            Hosted by {listing.event_hosted_by}
                          </p>
                        )}
                      </div>
                      
                      {/* Event Category Badge */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-white/70 mb-1 whitespace-nowrap">Event Type</p>
                        <Badge className="bg-white/20 text-white border-white/30 capitalize text-sm px-3 py-1 whitespace-nowrap">
                          {formatCategory(listing.category) || 'Event'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Venue & Ticket Info for Ticketmaster events */}
              {listing.listing_type === 'event' && (listing.venue_name || listing.ticket_url) && (
                <div className="mb-6 flex flex-wrap gap-3">
                  {listing.venue_name && (
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-4 py-2.5 text-sm" data-testid="event-venue-badge">
                      <MapPin className="h-4 w-4 text-[#0F2C3E]" />
                      <span className="text-slate-700"><span className="font-medium">{listing.venue_name}</span>{listing.town ? `, ${listing.town}` : ''}</span>
                    </div>
                  )}
                  {listing.ticket_url && listing.show_ticket_link !== false && (
                    <a href={listing.ticket_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#0F2C3E] text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-[#0F2C3E]/90 transition-colors"
                      data-testid="event-ticket-btn">
                      <ExternalLink className="h-4 w-4" />
                      Get Tickets
                    </a>
                  )}
                </div>
              )}

              <div className="prose max-w-none break-words" data-testid="listing-description">
                <RichTextRenderer content={listing.description} />
              </div>

              {/* Video Embed (Premium) — YouTube or Vimeo */}
              {listing.video_url && listing.plan === 'premium' && (() => {
                const extractYouTubeId = (url) => {
                  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
                  return m ? m[1] : null;
                };
                const extractVimeoId = (url) => {
                  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                  return m ? m[1] : null;
                };
                const ytId = extractYouTubeId(listing.video_url);
                const vimeoId = !ytId ? extractVimeoId(listing.video_url) : null;
                if (!ytId && !vimeoId) return null;
                const src = ytId
                  ? `https://www.youtube.com/embed/${ytId}`
                  : `https://player.vimeo.com/video/${vimeoId}`;
                return (
                  <div className="mt-6 pt-6 border-t" data-testid="listing-video-embed">
                    <h2 className="text-lg font-semibold text-slate-800 mb-3">Video</h2>
                    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-slate-900">
                      <iframe
                        src={src}
                        title={`${listing.title} — video`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0"
                        loading="lazy"
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Accessibility / Certifications / Featured Post appear in the
                  desktop right-hand sidebar as dedicated cards. On mobile
                  (where there is no persistent sidebar) we keep them inline
                  with the description so users still see them without
                  scrolling past reviews. */}
              {/* Accessibility (Enhanced & Premium) — mobile only */}
              {listing.accessibility_info && ['enhanced', 'premium'].includes(listing.plan) && (
                <div className="mt-6 pt-6 border-t lg:hidden" data-testid="listing-accessibility">
                  <h2 className="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <span aria-hidden="true">♿</span> Accessibility
                  </h2>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{listing.accessibility_info}</p>
                </div>
              )}

              {/* Certifications & Awards (Premium) — mobile only */}
              {Array.isArray(listing.certifications_awards) && listing.certifications_awards.length > 0 && listing.plan === 'premium' && (
                <div className="mt-6 pt-6 border-t lg:hidden" data-testid="listing-certifications">
                  <h2 className="text-lg font-semibold text-slate-800 mb-3">Certifications & Awards</h2>
                  <ul className="flex flex-wrap gap-2">
                    {listing.certifications_awards.map((cert, idx) => (
                      <li key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-sm text-amber-800">
                        <span aria-hidden="true">🏆</span>
                        {cert}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Featured Social Post (Premium) — mobile only */}
              {listing.plan === 'premium' && (
                <div className="lg:hidden">
                  <FeaturedSocialPosts
                    urls={listing.featured_social_posts}
                    onEngage={() => trackEngagement('featured_post')}
                  />
                </div>
              )}

              {listing.listing_type === 'business' && (listing.updated_at || listing.created_at) && (
                <p className="mt-4 text-xs text-slate-400 flex items-center gap-1" data-testid="freshness-indicator">
                  <RefreshCw className="h-3 w-3" />
                  {formatFreshness(listing.updated_at || listing.created_at)}
                </p>
              )}

              {/* FAQ Section — from user-created (faqs) or enrichment (seo_faqs) */}
              {listing.show_faqs !== false && (
                (listing.faqs && listing.faqs.length > 0) || (listing.seo_faqs && listing.seo_faqs.length > 0)
              ) && (
                <div className="mt-6 pt-6 border-t" data-testid="listing-faqs">
                  <h2 className="text-lg font-semibold text-slate-800 mb-3">Frequently Asked Questions</h2>
                  <div className="space-y-3">
                    {(listing.faqs && listing.faqs.length > 0 ? listing.faqs : listing.seo_faqs).map((faq, i) => (
                      <details key={i} className="group bg-slate-50 rounded-lg border border-slate-200">
                        <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900">
                          {faq.question}
                          <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-90" />
                        </summary>
                        <div className="px-4 pb-4 text-sm text-slate-600">{faq.answer}</div>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin: AI Enrich button */}
              {isAdmin && listing.listing_type === 'business' && (
                <div className="mt-6 pt-4 border-t border-dashed border-slate-200">
                  <button
                    onClick={() => setShowEnrichModal(true)}
                    className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors"
                    data-testid="enrich-listing-btn"
                  >
                    <Sparkles className="h-4 w-4" />
                    {listing.enriched_at ? 'Re-enrich with AI' : 'Enrich with AI'}
                    {listing.enriched_at && <span className="text-xs text-slate-400 ml-1">(enriched {new Date(listing.enriched_at).toLocaleDateString('en-GB')})</span>}
                  </button>
                </div>
              )}
            </div>
            )}

            {/* Job Description Section */}
            {listing.listing_type === 'job' && (
              <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-green-600" />
                    Job Description
                  </h2>
                  {(listing.posted_date || listing.created_at) && (
                    <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="job-posted-date">
                      <Clock className="h-3 w-3" />
                      Posted {new Date(listing.posted_date || listing.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
                <div className="prose max-w-none break-words" data-testid="listing-description">
                  <RichTextRenderer content={listing.description} />
                </div>
                
                {/* Application Link if available - job listings use the website field as application URL */}
                {listing.website && (
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <a 
                      href={listing.website.startsWith('http') ? listing.website : `https://${listing.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                      data-testid="job-apply-now-link"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Apply Now
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Claim Listing Section — for bulk-uploaded businesses */}
            <ClaimListing listing={listing} />

            {/* Associated Jobs Section for Business Listings */}
            {listing.listing_type === 'business' && associatedJobs.length > 0 && (
              <Card className="mb-8" data-testid="associated-jobs-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-[#0F2C3E]" />
                    Jobs at {listing.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {associatedJobs.map((job) => (
                      <Link
                        key={job.id}
                        to={getListingUrl(job)}
                        className="block p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`job-listing-${job.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-lg">{job.title}</h4>
                            <p className="text-sm text-slate-600 mt-1">{job.category}</p>
                            {job.salary && (
                              <p className="text-sm font-medium text-green-600 mt-1">{formatSalary(job.salary)}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {job.town}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mobile Contact Information - Shows only on mobile, after description and before reviews */}
            <div className="lg:hidden mt-8" data-testid="contact-section">
              <Card className="shadow-lg border-0 bg-white rounded-2xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-[#0F2C3E] to-[#1a4355] text-white">
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Company - only for job listings */}
                  {listing.listing_type === 'job' && listing.company && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-[#4A6741]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Company</p>
                        <p className="font-medium text-slate-900">{listing.company}</p>
                      </div>
                    </div>
                  )}
                  {/* Hosted By - only for event listings */}
                  {listing.listing_type === 'event' && listing.event_hosted_by && (
                    <div className="flex items-start gap-3" data-testid="mobile-contact-hosted-by">
                      <div className="w-10 h-10 rounded-full bg-[#7C3AED]/10 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-[#7C3AED]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Hosted By</p>
                        <p className="font-medium text-slate-900">{listing.event_hosted_by}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Address */}
                  {listing.address && listing.status !== 'sold' && listing.listing_type !== 'classified' && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#0F2C3E]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-[#0F2C3E]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Address</p>
                        <p className="font-medium text-slate-900" style={{textTransform: 'capitalize'}}>{listing.address}</p>
                        {listing.postcode && <p className="text-slate-600">{listing.postcode}</p>}
                      </div>
                    </div>
                  )}
                  
                  {/* Phone - with obfuscation */}
                  {listing.phone && listing.status !== 'sold' && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-[#4A6741]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Phone</p>
                        {!phoneRevealed ? (
                          <button
                            onClick={handleRevealPhone}
                            className="font-medium text-[#0F2C3E] hover:text-[#0F2C3E]/80 transition-colors flex items-center gap-2"
                          >
                            <span className="font-mono">{obfuscatePhone(listing.phone)}</span>
                            <span className="text-xs bg-[#0F2C3E] text-white px-2 py-0.5 rounded">Reveal</span>
                          </button>
                        ) : (
                          <a href={`tel:${listing.phone}`} className="font-medium text-slate-900 hover:text-[#0F2C3E] transition-colors" onClick={handleContactClick}>
                            {listing.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Email - with obfuscation (only if plan allows) */}
                  {listing.email && listing.status !== 'sold' && isPlanFeatureAllowed(listing, 'allow_email') && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#D4A017]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-[#D4A017]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
                        {emailRevealed ? (
                          <a href={`mailto:${listing.email}`} className="font-medium text-slate-900 hover:text-[#0F2C3E] transition-colors break-all">
                            {listing.email}
                          </a>
                        ) : (
                          <button
                            onClick={handleRevealEmail}
                            className="font-medium text-[#0F2C3E] hover:text-[#D4A017] transition-colors flex items-center gap-1"
                          >
                            <span className="font-mono">{obfuscateEmail(listing.email)}</span>
                            <span className="text-xs text-slate-500 ml-1">(click to reveal)</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Website (only if plan allows) — hidden on mobile for marketplace items */}
                  {listing.website && listing.status !== 'sold' && isPlanFeatureAllowed(listing, 'allow_website') && listing.listing_type !== 'classified' && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center flex-shrink-0">
                        {listing.listing_type === 'event' ? <Ticket className="h-5 w-5 text-[#4A6741]" /> : <Globe className="h-5 w-5 text-[#4A6741]" />}
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">{listing.listing_type === 'job' ? 'Application URL' : listing.listing_type === 'event' ? 'Tickets' : 'Website'}</p>
                        <a
                          href={listing.website.startsWith('http') ? listing.website : `https://${listing.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-[#0F2C3E] hover:underline break-all flex items-center gap-1"
                          onClick={handleContactClick}
                        >
                          {listing.listing_type === 'job' ? 'View Job & Apply' : listing.listing_type === 'event' ? 'Get Tickets' : 'Visit Website'}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Opening Hours - for business listings (only if plan allows) */}
                  {listing.listing_type === 'business' && listing.opening_hours && isPlanFeatureAllowed(listing, 'allow_opening_hours') && (() => {
                    const rows = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                      .map(day => ({ day, hours: listing.opening_hours[day] }))
                      .filter(({ hours }) => hours && (hours.closed || (hours.open && hours.close)));
                    if (rows.length === 0) return null;
                    return (
                    <div className="pt-4 mt-4 border-t">
                      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#0F2C3E]" />
                          <h4 className="font-medium text-slate-800">Opening Hours</h4>
                        </div>
                        <OpeningHoursStatus openingHours={listing.opening_hours} />
                      </div>
                      <div className="space-y-1.5">
                        {rows.map(({ day, hours }) => {
                          const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() === day;
                          return (
                            <div key={day} className={`flex justify-between text-sm py-1 px-2 rounded ${isToday ? 'bg-[#0F2C3E]/5 font-medium' : ''}`}>
                              <span className={`capitalize ${isToday ? 'text-[#0F2C3E]' : 'text-slate-600'}`}>
                                {day}
                                {isToday && <span className="ml-1 text-xs text-[#4A6741]">(Today)</span>}
                              </span>
                              <span className={hours.closed ? 'text-red-500' : 'text-slate-800'}>
                                {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}

                  {/* Social Media Links (Premium business listings) */}
                  {listing.listing_type === 'business' && listing.plan === 'premium' && listing.social_links && (
                    <SocialLinks socialLinks={listing.social_links} onClick={() => trackEngagement('social_profile')} />
                  )}

                  {/* Send Message Button for Marketplace */}
                  {listing.listing_type === 'classified' && user && listing.owner_id !== user.id && listing.status !== 'sold' && (
                    <div className="pt-4 mt-4 border-t">
                      {!showMessageForm ? (
                        <Button 
                          onClick={() => setShowMessageForm(true)}
                          className="w-full bg-[#4A6741] hover:bg-[#4A6741]/90"
                          data-testid="send-message-btn"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Send Message
                        </Button>
                      ) : (
                        <form onSubmit={handleSendMessage} className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {quickMessages.map((msg, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setMessageContent(msg)}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                  messageContent === msg 
                                    ? 'bg-[#4A6741] text-white border-[#4A6741]' 
                                    : 'bg-white text-slate-600 border-slate-300 hover:border-[#4A6741] hover:text-[#4A6741]'
                                }`}
                              >
                                {msg}
                              </button>
                            ))}
                          </div>
                          <Textarea
                            value={messageContent}
                            onChange={(e) => setMessageContent(e.target.value)}
                            placeholder="Write your message to the seller..."
                            maxLength={500}
                            rows={3}
                            required
                          />
                          <div className="flex gap-2">
                            <Button 
                              type="submit" 
                              disabled={sendingMessage || !messageContent.trim()}
                              className="flex-1 bg-[#4A6741] hover:bg-[#4A6741]/90"
                            >
                              {sendingMessage ? 'Sending...' : 'Send'}
                            </Button>
                            <Button 
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowMessageForm(false);
                                setMessageContent('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}

                  {/* Login prompt for non-logged-in users on marketplace listings */}
                  {listing.listing_type === 'classified' && !user && (
                    <div className="pt-4 mt-4 border-t">
                      <Button className="w-full" variant="outline" onClick={() => { setSignUpVariant('message'); setShowSignUpModal(true); }}>
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Login to Send Message
                        </Button>
                    </div>
                  )}

                  {/* Category and Town */}
                  <div className="pt-4 mt-4 border-t space-y-3">
                    {listing.listing_type === 'classified' && listing.classified_price && listing.status !== 'sold' && (
                      <div className="flex items-center justify-between" data-testid="mobile-contact-price">
                        <span className="text-sm text-slate-600">Price</span>
                        <span className="text-sm font-semibold text-slate-900">£{Number(listing.classified_price).toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Category</span>
                      <Badge variant="secondary">{formatCategory(listing.category)}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Location</span>
                      <Badge variant="outline">{listing.is_outside_essex && listing.region && listing.region !== 'Essex' ? listing.region : listing.town}</Badge>
                    </div>
                  </div>

                  {/* Share Buttons */}
                  <div className="pt-4 mt-4 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Share2 className="h-4 w-4" />
                      Share this listing
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleShareFacebook}
                        className="flex-1 text-[#1877F2] border-[#1877F2]/30 hover:bg-[#1877F2]/10"
                      >
                        <Facebook className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleShareTwitter}
                        className="flex-1 text-black border-slate-300 hover:bg-slate-100"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleShareWhatsApp}
                        className="flex-1 text-[#25D366] border-[#25D366]/30 hover:bg-[#25D366]/10"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyLink}
                        className="flex-1"
                        data-testid="share-copy-btn"
                      >
                        {linkCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleShareEmail}
                        className="flex-1 text-slate-600 border-slate-300 hover:bg-slate-100"
                        data-testid="share-email-btn"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Report this listing — subtle link below sidebar */}
            <div className="text-center mt-2">
              <button
                onClick={handleOpenReport}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1 mx-auto"
                data-testid="report-listing-btn"
              >
                <Flag className="h-3 w-3" />
                Report this listing
              </button>
            </div>
            {/* Review Prompt - soft nudge after 30s engagement or watchlist add */}
            {showReviewPrompt && listing.listing_type === 'business' && user && listing.owner_id !== user.id && (
              <div
                className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500"
                data-testid="review-prompt"
              >
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Star className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">Have you visited {listing.title}?</p>
                  <p className="text-xs text-slate-500">Share your experience to help others in the community.</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                    onClick={() => {
                      reviewPromptDismissed.current = true;
                      setShowReviewPrompt(false);
                      document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    data-testid="review-prompt-write-btn"
                  >
                    Write a Review
                  </Button>
                  <button
                    className="text-slate-400 hover:text-slate-600 p-1"
                    onClick={() => { reviewPromptDismissed.current = true; setShowReviewPrompt(false); }}
                    aria-label="Dismiss review prompt"
                    data-testid="review-prompt-dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            {listing.listing_type === 'business' && (
              <div id="reviews" className="mt-8 pt-8 border-t">
                {/* Leave a Review form - only show if logged in and NOT the listing owner */}
                {user && listing.owner_id !== user.id && (
                  <Card className="mb-8">
                    <CardHeader>
                      <CardTitle>Leave a Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleReviewSubmit} data-testid="review-form">
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-2">Rating</label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setRating(star)}
                                data-testid={`rating-star-${star}`}
                              >
                                <Star
                                  className={`h-8 w-8 cursor-pointer transition-colors ${star <= rating ? 'fill-[#D4A017] text-[#D4A017]' : 'text-slate-300 hover:text-slate-400'}`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-2">Comment</label>
                          <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Share your experience..."
                            required
                            rows={4}
                            data-testid="review-comment"
                          />
                        </div>
                        <Button type="submit" className="bg-[#0F2C3E]" data-testid="submit-review-btn">
                          Submit Review
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}
                
                {/* Show login prompt for non-logged-in users */}
                {!user && (
                  <Card className="mb-8">
                    <CardHeader>
                      <CardTitle>Leave a Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 mb-4">Please log in to leave a review.</p>
                      <Button onClick={() => navigate('/login')} className="bg-[#0F2C3E]">
                        Log In to Review
                      </Button>
                    </CardContent>
                  </Card>
                )}

                <div>
                  <h2 className="text-2xl font-semibold mb-4">Reviews</h2>
                  {reviews.length > 0 ? (
                    <div className="space-y-4">
                      {reviews.map((review) => (
                        <Card key={review.id} data-testid={`review-${review.id}`}>
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold">{review.user_name}</p>
                                {renderStars(review.rating)}
                              </div>
                              <p className="text-sm text-slate-500">
                                {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <p className="text-slate-700">{review.comment}</p>
                            {review.owner_response && (
                              <div className="mt-4 pl-4 border-l-2 border-slate-200">
                                <p className="text-sm font-semibold mb-1">Owner Response</p>
                                <p className="text-sm text-slate-600">{review.owner_response}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-600">No reviews yet. Be the first to review!</p>
                  )}
                </div>
              </div>
            )}

            {/* Similar Items Section — Internal Linking for SEO */}
            {similarItems.length > 0 && (() => {
              const allSameCat = similarItems.every(it => it.category === listing.category);
              const allSameTown = similarItems.every(it => it.town === listing.town);
              let similarTitle;
              if (listing.listing_type === 'event') {
                similarTitle = 'More Events in Essex';
              } else if (listing.listing_type === 'job') {
                similarTitle = 'More Jobs in Essex';
              } else if (allSameCat && allSameTown && listing.town) {
                similarTitle = `More ${formatCategory(listing.category)} in ${listing.town}`;
              } else if (allSameCat) {
                similarTitle = `More ${formatCategory(listing.category)}`;
              } else {
                similarTitle = 'You might also like';
              }
              return (
              <div className="mt-8 pt-8 border-t">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-900">
                    {similarTitle}
                  </h2>
                  {listing.town && listing.category && (
                    <Link
                      to={listing.listing_type === 'business' ? `/business/${slugify(listing.category)}?town=${encodeURIComponent(listing.town)}` : `/${listing.listing_type === 'job' ? 'jobs' : listing.listing_type === 'event' ? 'events' : 'marketplace'}?category=${encodeURIComponent(listing.category)}&town=${encodeURIComponent(listing.town)}`}
                      className="text-sm text-[#4A6741] hover:text-[#0F2C3E] font-medium transition-colors"
                      data-testid="browse-more-link"
                    >
                      Browse all &rarr;
                    </Link>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {similarItems.slice(0, 6).map((item) => (
                    <ListingCard key={item.id} listing={item} />
                  ))}
                </div>
              </div>
              );
            })()}
          </div>

          {/* Sidebar - scrolls with the page, hidden on mobile */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="space-y-6">
              <Card className="shadow-lg border-0 bg-white rounded-2xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-[#0F2C3E] to-[#1a4355] text-white">
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Company - only for job listings */}
                  {listing.listing_type === 'job' && listing.company && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-[#4A6741]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Company</p>
                        <p className="font-medium text-slate-900">{listing.company}</p>
                      </div>
                    </div>
                  )}
                  {/* Hosted By - only for event listings */}
                  {listing.listing_type === 'event' && listing.event_hosted_by && (
                    <div className="flex items-start gap-3" data-testid="desktop-contact-hosted-by">
                      <div className="w-10 h-10 rounded-full bg-[#7C3AED]/10 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-[#7C3AED]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Hosted By</p>
                        <p className="font-medium text-slate-900">{listing.event_hosted_by}</p>
                      </div>
                    </div>
                  )}
                  {/* Address - show if available (admin can add to any listing) */}
                  {listing.address && listing.status !== 'sold' && 
                   listing.listing_type !== 'classified' && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#0F2C3E]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-[#0F2C3E]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Address</p>
                        <p className="font-medium text-slate-900" style={{textTransform: 'capitalize'}}>{listing.address}</p>
                        {listing.postcode && <p className="text-sm text-slate-600">{listing.postcode}</p>}
                    </div>
                  </div>
                )}
                {listing.phone && listing.status !== 'sold' && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-[#4A6741]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Phone</p>
                      {!phoneRevealed ? (
                        <button
                          onClick={handleRevealPhone}
                          className="font-medium text-[#0F2C3E] hover:text-[#0F2C3E]/80 transition-colors flex items-center gap-2"
                          data-testid="reveal-phone-btn"
                        >
                          <span className="font-mono">{obfuscatePhone(listing.phone)}</span>
                          <span className="text-xs bg-[#0F2C3E] text-white px-2 py-0.5 rounded">Reveal</span>
                        </button>
                      ) : (
                        <a href={`tel:${listing.phone}`} className="font-medium text-slate-900 hover:text-[#0F2C3E] transition-colors" onClick={handleContactClick} data-testid="contact-phone">
                          {listing.phone}
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {listing.email && listing.status !== 'sold' && isPlanFeatureAllowed(listing, 'allow_email') && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4A017]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-[#D4A017]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Email</p>
                      {emailRevealed ? (
                        <a href={`mailto:${listing.email}`} className="font-medium text-slate-900 hover:text-[#0F2C3E] transition-colors" data-testid="contact-email">
                          {listing.email}
                        </a>
                      ) : (
                        <button
                          onClick={handleRevealEmail}
                          className="font-medium text-[#0F2C3E] hover:text-[#D4A017] transition-colors flex items-center gap-1"
                          data-testid="reveal-email-btn"
                        >
                          <span className="font-mono">{obfuscateEmail(listing.email)}</span>
                          <span className="text-xs text-slate-500 ml-1">(click to reveal)</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {listing.website && listing.status !== 'sold' && isPlanFeatureAllowed(listing, 'allow_website') && listing.listing_type !== 'classified' && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      {listing.listing_type === 'event' ? <Ticket className="h-5 w-5 text-blue-600" /> : <Globe className="h-5 w-5 text-blue-600" />}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">{listing.listing_type === 'job' ? 'Application URL' : listing.listing_type === 'event' ? 'Tickets' : 'Website'}</p>
                      <a
                        href={listing.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-slate-900 hover:text-[#0F2C3E] flex items-center gap-1 transition-colors"
                        onClick={handleContactClick}
                        data-testid="contact-website"
                      >
                        {listing.listing_type === 'job' ? 'View Job & Apply' : listing.listing_type === 'event' ? 'Get Tickets' : 'Visit Website'}
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                )}
{/* Event Date and Salary now displayed in dedicated info cards above */}

                {/* Opening Hours - for business listings with opening_hours data (only if plan allows) */}
                {listing.listing_type === 'business' && listing.opening_hours && isPlanFeatureAllowed(listing, 'allow_opening_hours') && (() => {
                  const rows = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                    .map(day => ({ day, hours: listing.opening_hours[day] }))
                    .filter(({ hours }) => hours && (hours.closed || (hours.open && hours.close)));
                  if (rows.length === 0) return null;
                  return (
                  <div className="pt-4 mt-4 border-t">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-4 w-4 text-[#0F2C3E]" />
                      <h4 className="font-medium text-slate-800">Opening Hours</h4>
                    </div>
                    <div className="space-y-1.5">
                      {rows.map(({ day, hours }) => {
                        const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() === day;
                        return (
                          <div key={day} className={`flex justify-between text-sm py-1 px-2 rounded ${isToday ? 'bg-[#0F2C3E]/5 font-medium' : ''}`}>
                            <span className={`capitalize ${isToday ? 'text-[#0F2C3E]' : 'text-slate-600'}`}>
                              {day}
                              {isToday && <span className="ml-1 text-xs text-[#4A6741]">(Today)</span>}
                            </span>
                            <span className={hours.closed ? 'text-red-500' : 'text-slate-800'}>
                              {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })()}

                {/* Send Message Button for Marketplace */}
                {listing.listing_type === 'classified' && user && listing.owner_id !== user.id && listing.status !== 'sold' && (
                  <div className="pt-4 mt-4 border-t">
                    {!showMessageForm ? (
                      <Button 
                        onClick={() => setShowMessageForm(true)}
                        className="w-full bg-[#4A6741] hover:bg-[#4A6741]/90"
                        data-testid="send-message-btn"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Send Message
                      </Button>
                    ) : (
                      <form onSubmit={handleSendMessage} className="space-y-3">
                        {/* Quick Message Templates */}
                        <div className="flex flex-wrap gap-2">
                          {quickMessages.map((msg, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setMessageContent(msg)}
                              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                messageContent === msg 
                                  ? 'bg-[#4A6741] text-white border-[#4A6741]' 
                                  : 'bg-white text-slate-600 border-slate-300 hover:border-[#4A6741] hover:text-[#4A6741]'
                              }`}
                            >
                              {msg}
                            </button>
                          ))}
                        </div>
                        <Textarea
                          value={messageContent}
                          onChange={(e) => setMessageContent(e.target.value)}
                          placeholder="Write your message to the seller..."
                          maxLength={500}
                          rows={3}
                          required
                          data-testid="message-textarea"
                        />
                        <div className="flex gap-2">
                          <Button 
                            type="submit" 
                            disabled={sendingMessage || !messageContent.trim()}
                            className="flex-1 bg-[#4A6741] hover:bg-[#4A6741]/90"
                            data-testid="submit-message-btn"
                          >
                            {sendingMessage ? 'Sending...' : 'Send'}
                          </Button>
                          <Button 
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setShowMessageForm(false);
                              setMessageContent('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Messages are limited to 10 per conversation. Be respectful.
                        </p>
                      </form>
                    )}
                  </div>
                )}

                {/* Login prompt for non-logged-in users on marketplace listings */}
                {listing.listing_type === 'classified' && !user && (
                  <div className="pt-4 mt-4 border-t">
                    <Button className="w-full" variant="outline" data-testid="login-to-message" onClick={() => { setSignUpVariant('message'); setShowSignUpModal(true); }}>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Login to Send Message
                      </Button>
                  </div>
                )}
                
                {/* Category and Town — Internal SEO links */}
                <div className="pt-4 mt-4 border-t space-y-3">
                  {listing.listing_type === 'classified' && listing.classified_price && listing.status !== 'sold' && (
                    <div className="flex items-center justify-between" data-testid="desktop-contact-price">
                      <span className="text-sm text-slate-600">Price</span>
                      <span className="text-sm font-semibold text-slate-900">£{Number(listing.classified_price).toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Category</span>
                    <Link to={listing.listing_type === 'business' ? `/business/${slugify(listing.category)}` : `/${listing.listing_type === 'job' ? 'jobs' : listing.listing_type === 'event' ? 'events' : 'marketplace'}?category=${encodeURIComponent(listing.category)}`}>
                      <Badge variant="secondary" className="cursor-pointer hover:bg-slate-200 transition-colors">{formatCategory(listing.category)}</Badge>
                    </Link>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Location</span>
                    {listing.is_outside_essex && listing.region && listing.region !== 'Essex' ? (
                      <Badge variant="outline">{listing.region}</Badge>
                    ) : (
                      <Link to={`/town/${listing.town?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}>
                        <Badge variant="outline" className="cursor-pointer hover:bg-slate-100 transition-colors">{listing.town}</Badge>
                      </Link>
                    )}
                  </div>
                  {listing.listing_type === 'business' && listing.town && listing.category && (
                    <Link
                      to={`/services/${listing.category.toLowerCase().replace(/&/g, 'and').replace(/['']/g, '').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}/${listing.town.toLowerCase().replace(/&/g, 'and').replace(/['']/g, '').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}`}
                      className="block text-xs text-center text-[#4A6741] hover:text-[#0F2C3E] transition-colors mt-1"
                      data-testid="niche-page-link"
                    >
                      View all {formatCategory(listing.category)} in {listing.town}
                    </Link>
                  )}
                </div>

                {/* Claim on Google — Business listings only */}
                {listing.listing_type === 'business' && listing.verified && (
                  <div className="pt-4 mt-4 border-t">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <svg className="h-6 w-6 mt-0.5 flex-shrink-0" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 mb-1">Claim on Google Business Profile</p>
                          <p className="text-xs text-slate-500 mb-2">Get found on Google Maps and Search. Free to set up.</p>
                          <a
                            href={`https://business.google.com/create?fp=1&gmbsrc=gb-en-et-ip-z-gmb-s-z-l~mrc%7Cclaimbz&hl=en-GB&psel=0&q=${encodeURIComponent(listing.title + ' ' + (listing.address || '') + ' ' + (listing.town || 'Essex'))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4285F4] hover:text-[#3367d6] transition-colors"
                            data-testid="claim-on-google-link"
                            onClick={() => { try { window.gtag?.('event', 'claim_on_google_click', { listing_id: listing.id, listing_title: listing.title }); } catch(e){} }}
                          >
                            Claim this business
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Social Media Links — Premium business listings, desktop sidebar */}
                {listing.listing_type === 'business' && listing.plan === 'premium' && listing.social_links && (
                  <SocialLinks socialLinks={listing.social_links} onClick={() => trackEngagement('social_profile')} />
                )}

                {/* Share Buttons */}
                <div className="pt-4 mt-4 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Share this listing
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShareFacebook}
                      className="flex-1 text-[#1877F2] border-[#1877F2]/30 hover:bg-[#1877F2]/10"
                    >
                      <Facebook className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShareTwitter}
                      className="flex-1 text-black border-slate-300 hover:bg-slate-100"
                    >
                      {/* X (formerly Twitter) logo */}
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShareWhatsApp}
                      className="flex-1 text-[#25D366] border-[#25D366]/30 hover:bg-[#25D366]/10"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyLink}
                      className="flex-1"
                    >
                      {linkCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShareEmail}
                      className="flex-1 text-slate-600 border-slate-300 hover:bg-slate-100"
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Certifications & Awards (Premium — desktop sidebar) */}
            {Array.isArray(listing.certifications_awards) && listing.certifications_awards.length > 0 && listing.plan === 'premium' && (
              <Card data-testid="listing-certifications-sidebar">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span aria-hidden="true">🏆</span> Certifications & Awards
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="flex flex-wrap gap-2">
                    {listing.certifications_awards.map((cert, idx) => (
                      <li key={idx} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs text-amber-800">
                        {cert}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Featured Social Post (Premium — desktop sidebar) */}
            {listing.plan === 'premium' && Array.isArray(listing.featured_social_posts) && listing.featured_social_posts.length > 0 && (
              <Card data-testid="listing-featured-post-sidebar">
                <CardContent className="p-4">
                  <FeaturedSocialPosts
                    urls={listing.featured_social_posts}
                    variant="card"
                    onEngage={() => trackEngagement('featured_post')}
                  />
                </CardContent>
              </Card>
            )}

            {/* Accessibility (Enhanced & Premium — desktop sidebar) */}
            {listing.accessibility_info && ['enhanced', 'premium'].includes(listing.plan) && (
              <Card data-testid="listing-accessibility-sidebar">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span aria-hidden="true">♿</span> Accessibility
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{listing.accessibility_info}</p>
                </CardContent>
              </Card>
            )}

            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-[#0F2C3E] to-[#1a4355] text-white border-0">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-lg mb-2">Have something to list?</h3>
                <p className="text-white/80 text-sm mb-4">
                  Create your own listing and reach thousands of people across Essex.
                </p>
                <Link to="/pricing">
                  <Button className="w-full bg-white text-[#0F2C3E] hover:bg-white/90" data-testid="cta-create-listing">
                    Create Your Listing
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Advertisement */}
            <SidebarAd />
            </div>
          </div>
        </div>
      </div>
      <SignUpPromptModal isOpen={showSignUpModal} onClose={() => setShowSignUpModal(false)} variant={signUpVariant} />

      {/* Report Listing Modal */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && setReportOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" data-testid="report-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Flag className="h-5 w-5 text-red-500" />
                <h3 className="font-semibold text-slate-900">Report this listing</h3>
              </div>
              <button onClick={() => setReportOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {reportDone ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <p className="font-medium text-slate-900 mb-1">Report received</p>
                <p className="text-sm text-slate-500">Thank you — our team will review this listing shortly.</p>
                <button onClick={() => setReportOpen(false)} className="mt-4 text-sm text-slate-500 underline">Close</button>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-4">
                  Help us keep Discover Essex safe and relevant. Select the reason that best describes the issue.
                </p>

                <div className="space-y-2 mb-4">
                  {REPORT_REASONS.map(({ value, label }) => (
                    <label key={value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${reportReason === value ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="report_reason"
                        value={value}
                        checked={reportReason === value}
                        onChange={() => setReportReason(value)}
                        className="text-red-500"
                        data-testid={`report-reason-${value}`}
                      />
                      <span className="text-sm text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Additional details <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value.slice(0, 500))}
                    placeholder="Any other information that might help our review..."
                    rows={3}
                    className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-200"
                    data-testid="report-details-input"
                  />
                  <p className="text-xs text-slate-400 text-right mt-1">{reportDetails.length}/500</p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setReportOpen(false)}
                    className="flex-1 py-2 px-4 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitReport}
                    disabled={!reportReason || reportSubmitting}
                    className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="report-submit-btn"
                  >
                    {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>

                <p className="text-xs text-slate-400 text-center mt-3">
                  Reports are reviewed by our team. Misuse may result in account suspension.
                </p>
              </>
            )}
          </div>
        </div>
      )}


      {/* AI Enrichment Modal */}
      {showEnrichModal && listing && (
        <EnrichmentModal
          listing={listing}
          onClose={() => setShowEnrichModal(false)}
          onApplied={() => {
            setShowEnrichModal(false);
            // Refresh listing data
            axios.get(`${API}/listings/${listing.id}`).then(res => setListing(res.data)).catch(() => {});
          }}
        />
      )}

      {/* Mobile Quick Actions - sticky bottom bar */}
      <MobileQuickActions listing={listing} />

      {/* Recently Viewed + Also Viewed */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 pb-8">
        <RecentlyViewedSection currentListingId={listing.id} />
      </div>
    </div>
  );
};

export default ListingDetail;